import { getGuid, compareArrays, deepClone } from './../helpers/utility';
import { reorderItems, moveItem, deleteItem, updateProperty, moveItemsByKeyPath } from './utility';
import {
  getDefaultSettingType,
  getField,
  getItemIndexId,
  getFieldPositionKeyPathsByFieldId,
  getDataLayoutPositionKeyPathsByFieldId,
  getFieldPositionKeyPathsByCheckBoxItemId,
  getDataLayoutPositionItemKeyPathsByCheckBoxItemId,
  getColumn,
  getSelectedFieldPositions,
  getFieldKeyPathById,
  getFieldPositionDefaultSettings,
  getFieldDefaultSettings,
  getHighestTabIndex,
  getColumnPositionKeyPathsByColumnId,
  getSelecteBasicElements,
  GetDefaultTemplateSettings
} from './selectors';
import {
  getDefaultName,
  adjustRowHeightsByOffset,
  restoreFieldSelection,
  getFieldPositionKeyPathsFromSelection,
  getSelectedFieldPositionsKeyPaths,
  getSelectedPositionFieldIds,
  getFieldKeyPathsFromFieldIds,
  getUnmappedFieldsFromSelection,
  getFieldKeyPathFromSelectedField,
  getParentKeyPath,
  getParentKeyPaths,
  getParentKeyPathByLevel
} from './helpers';
import { deleteIn, getIn, insertAt, intersectRect, setIn } from './../helpers/helpers';
import {
  FieldTypes,
  DisplayType,
  BasicElementActionTypes,
  TemplateFileTypes,
  BasicElementTypes,
  BasicElementGroupAlignAction,
  BasicElementComputedProperties,
  EditorTypes
} from './../helpers/constants';
import { InitialsAspectRatio, SignatureAspectRatio } from '../formeditor/esignatureHelper';
import { analyticsTrack } from '../helpers/AnalyticsUtility';
import {
  BoundingRectangle,
  TemplateClipboard,
  KeyPath,
  TemplateEditorPayload,
  TemplateState,
  TemplateDateFormat,
  TemplateFeature,
  TemplateTableFieldProperties
} from './templateeditor-types';
import {
  BasicElement,
  CheckboxGroupItem,
  DataLayoutPosition,
  DataLayoutSection,
  DataLayoutTab,
  FieldPosition,
  PositionBase,
  TableColumnPosition,
  TableRowPosition,
  TemplateColumn,
  TemplateField,
  TemplateFormula,
  TemplatePage
} from '../types/template-types';
import { Identifiable } from 'gf-types';
import { fixDotNetDateFormat } from '../helpers/DateTimeUtility';
import { mapCriteriaToFormula } from './components/FieldValidations/helpers';
import { removeTemplateValidations } from './components/FieldValidations/updaters';

const DEFAULT_PASTE_OFFSET = 20;

export function updateTemplatePages(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  state.currentPageId = payload.currentPageId;
  const indexOfCurrentPageId = state?.template?.pages?.findIndex((page) => page.id === state.currentPageId);
  state = updateProperty(state, payload);
  state.currentPageId =
    indexOfCurrentPageId == null || indexOfCurrentPageId === -1 ? state?.template?.pages[0]?.id : state?.template?.pages[indexOfCurrentPageId]?.id;
  return state;
}

export function moveDataLayoutSection(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { previousTabKeyPath, newTabKeyPath, id, insertDirection } = payload;
  let insertNextToId = payload.insertNextToId;
  const previousTabKeyFullPath: KeyPath = [...previousTabKeyPath, 'sections'];
  const newTabKeyFullPath: KeyPath = [...newTabKeyPath, 'sections'];

  // if insertNextToId is null that means we just dropped it in the TabView
  if (!insertNextToId) {
    const sections = getIn<DataLayoutSection[]>(state, newTabKeyFullPath);
    if (sections.length > 0) {
      insertNextToId = insertDirection === 'up' ? sections[0].id : sections[sections.length - 1].id;
    }
  }

  state = moveItem(state, previousTabKeyFullPath, newTabKeyFullPath, 'sortNumber', id, insertNextToId, insertDirection);
  // re-select the section
  const newTabSections = getIn<DataLayoutSection[]>(state, newTabKeyFullPath);
  const newSelectionKeyPath: KeyPath = [...newTabKeyFullPath, ...[newTabSections.findIndex((p) => p.id === id)]];

  state.selection = { dataLayoutSections: [newSelectionKeyPath] };
  state.currentTabKeyPath = newTabKeyPath;

  return state;
}

export function moveDataLayoutTab(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { id, insertDirection } = payload;
  let insertNextToId = payload.insertNextToId;
  const dataLayoutTabsFullPath: KeyPath = ['template', 'dataLayouts', 0, 'dataLayoutTabs'];

  // if insertNextToId is null that means we just dropped it in the TabView
  if (!insertNextToId) {
    const tabs = getIn<DataLayoutTab[]>(state, dataLayoutTabsFullPath);
    if (tabs.length > 0) {
      insertNextToId = insertDirection === 'left' ? tabs[0].id : tabs[tabs.length - 1].id;
    }
  }

  return moveItem(state, dataLayoutTabsFullPath, dataLayoutTabsFullPath, 'sortNumber', id, insertNextToId, insertDirection);
}

export function deleteDataLayoutTab(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  deleteItem(state, payload.tabKeyPath);
  const tabsKeyPath: KeyPath = ['template', 'dataLayouts', 0, 'dataLayoutTabs'];

  const tabs = getIn<DataLayoutTab[]>(state, tabsKeyPath);
  if (tabs.length === 0) {
    addDefaultDataLayoutTab(state, tabsKeyPath);
  }

  return state;
}

export function duplicateDataLayoutTab(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { tabKeyPath } = payload;
  const fields = getIn<TemplateField[]>(state, ['template', 'fields']);
  const originalTab = getIn<DataLayoutTab>(state, tabKeyPath);
  const tabIndex = tabKeyPath.pop() as number;
  const copiedFieldMapping: { [fieldId: string]: TemplateField } = {};
  const originalFields: { [fieldId: string]: TemplateField } = {};

  for (const section of originalTab.sections) {
    for (const position of section.positions) {
      const field = getField(state, position.fieldId);

      // There could be multiple layouts that use the same field, so check if it's already been copied
      if (field && !Object.prototype.hasOwnProperty.call(copiedFieldMapping, field.id)) {
        const copiedField = copyField(field, fields);

        fields.push(copiedField);
        copiedFieldMapping[field.id] = copiedField;
        originalFields[field.id] = field;
      }
    }
  }

  const newSections: DataLayoutSection[] = [];

  for (const section of originalTab.sections) {
    const newPositions: DataLayoutPosition[] = [];

    for (const position of section.positions) {
      newPositions.push(copyDataLayoutPosition(position, copiedFieldMapping[position.fieldId], originalFields[position.fieldId]));
    }

    newSections.push({
      id: getGuid(),
      name: section.name,
      description: section.description,
      sortNumber: section.sortNumber,
      positions: reorderItems(newPositions, 'sortNumber')
    });
  }

  setIn(state, ['template', 'fields'], fields);

  let tabs = getIn<DataLayoutTab[]>(state, tabKeyPath) ?? [];

  tabs = insertAt<DataLayoutTab>(tabs, tabIndex + 1, {
    id: getGuid(),
    name: `${originalTab.name} Copy`,
    sortNumber: tabIndex + 1,
    sections: newSections
  });

  const reorderedItems = reorderItems(tabs, 'sortNumber');

  setIn(state, tabKeyPath, reorderedItems);
  state.currentTabKeyPath = [...tabKeyPath, ...[tabIndex + 1]];

  return state;
}

export function deleteItems(state: TemplateState, keyPaths?: KeyPath[]): TemplateState {
  if (keyPaths) {
    // we need to get the actual Ids of the items we're deleting because the indexes will change as we delete the siblings
    const itemMapping = [];

    for (const keyPath of keyPaths) {
      const item = getIn<PositionBase>(state, keyPath);

      if (itemMapping.findIndex((x) => x.id == item.id) == -1) {
        itemMapping.push({
          parentKeyPath: getParentKeyPath(keyPath),
          id: item.id
        });
      }
    }

    // delete selected positions
    for (const itemMap of itemMapping) {
      const parent = getIn<Identifiable[]>(state, itemMap.parentKeyPath);
      const keyPathToDelete = [...itemMap.parentKeyPath, ...[getItemIndexId(parent, itemMap.id)]];

      state = deleteItem(state, keyPathToDelete);
    }
  }

  return state;
}

export function updateFieldPositionsWithDefaultState(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const fieldPositionsWithDefaultState: KeyPath[] = payload.fieldPositionsWithDefaultState;

  return updateFieldPositionsWithDefaultFieldPositionSettings(state, fieldPositionsWithDefaultState, false);
}

export function updateSelectedFieldPositionToUseDefaultState(state: TemplateState): TemplateState {
  const keyPaths: KeyPath[] = state.selection.fieldPositions;

  return updateFieldPositionsWithDefaultFieldPositionSettings(state, keyPaths, true);
}

export function updateFieldPositionsToUseCustomFieldPositionSettings(state: TemplateState): TemplateState {
  const keyPaths: KeyPath[] = state.selection.fieldPositions;

  if (!!keyPaths) {
    for (const keyPath of keyPaths) {
      setIn(state, [...keyPath, 'useDefaultDisplaySettings'], false);
    }
  }

  return state;
}

export function unmapSelectedItems(state: TemplateState): TemplateState {
  // Store the selected field IDs
  const selectedFieldIds = [...(state.selection.fieldIds ?? [])];

  // Get the key paths for the selected field instances
  const fieldPositionKeyPaths = getSelectedFieldPositionsKeyPaths(state);

  // Get the key paths of form instances related to selected field
  const fieldKeyPaths = getFieldPositionKeyPathsFromSelection(state);

  const keyPaths = [...fieldPositionKeyPaths, ...fieldKeyPaths];

  // Remove all links to the selected fields (i.e. unmap them)
  const newState = deleteItems(state, keyPaths);

  // Keep the field selection after unmap
  return restoreFieldSelection(newState, selectedFieldIds);
}

export function deleteSelectedItems(state: TemplateState): TemplateState {
  let keyPaths: KeyPath[] = getSelectedFieldPositionsKeyPaths(state);
  const selectedPositionFieldIds = getSelectedPositionFieldIds(state);

  if (!selectedPositionFieldIds || selectedPositionFieldIds.length === 0) {
    const fieldPositionKeyPaths = getFieldPositionKeyPathsFromSelection(state);
    const fieldKeyPath = getFieldKeyPathFromSelectedField(state);

    const selectedFieldPositionKeyPaths = [...fieldPositionKeyPaths, ...fieldKeyPath];
    keyPaths = keyPaths.concat(selectedFieldPositionKeyPaths);
  } else {
    // if a selected field has no other instances before deletion, it will become 'unmapped'.
    // this will remove any unmapped fields to completely delete selected fields and their associated instances
    const selectedUnmappedFields = getUnmappedFieldsFromSelection(state, selectedPositionFieldIds);
    const unmappedFieldKeyPaths = getFieldKeyPathsFromFieldIds(state, selectedUnmappedFields);
    keyPaths = keyPaths.concat(unmappedFieldKeyPaths);
  }

  //the selected items above can only be deleted in groups
  //however, basic elements can always just be deleted
  if (state.selection.elements) {
    removeBasicElements(state, { keyPaths: state.selection.elements });
  }

  return deleteItems(state, keyPaths);
}

export function copySelectedItems(state: TemplateState): TemplateState {
  const fieldPositionKeyPaths = state.selection.fieldPositions || [];
  const elementsKeyPaths = state.selection.elements || [];

  state.clipboard = state.clipboard || {};
  state.clipboard.boundingRectangle = null;
  state.clipboard.pageId = null;

  if (fieldPositionKeyPaths.length > 0 || elementsKeyPaths.length > 0) {
    const fieldPositions = fieldPositionKeyPaths?.map((fieldPositionKeyPath) => getIn<FieldPosition>(state, fieldPositionKeyPath)) || [];
    const elements = elementsKeyPaths?.map((elementKeyPath) => getIn<BasicElement>(state, elementKeyPath)) || [];

    const boundingRectangle = getBoundingRectangle(fieldPositions, elements);

    state.clipboard.boundingRectangle = boundingRectangle;
    state.clipboard.isVisible = true; // Issue with this is if they copy field is not visible

    const pageKeyPath =
      fieldPositionKeyPaths && fieldPositionKeyPaths.length > 0
        ? fieldPositionKeyPaths[0]
        : elementsKeyPaths && elementsKeyPaths.length > 0
        ? elementsKeyPaths[0]
        : null;

    state.clipboard.pageId = getIn(state, [...getParentKeyPath(getParentKeyPath(pageKeyPath)), 'id']);
  }

  return state;
}

export function pasteSelectedItems(state: TemplateState, clipboard: TemplateClipboard, replicateFields = false): TemplateState {
  const { currentTemplateView, activeEditor } = state;

  if ((currentTemplateView === 0 && !clipboard.fieldPositions && !clipboard.elements) || (currentTemplateView === 1 && !clipboard.dataLayoutPositions)) {
    // only allow pasting when on the same view as the copied data was on
    return state;
  }
  state.selection ??= {};
  const fields = [...state.template.fields] as TemplateField[];
  const fieldMapping: Record<string, TemplateField> = {};
  const originalFields: Record<string, TemplateField> = {};

  if (clipboard.fields) {
    if (replicateFields) {
      for (const field of clipboard.fields) {
        if ([FieldTypes.eSignature, FieldTypes.eSignatureInitials].includes(field.type)) {
          continue;
        }
        const copiedField = copyField(field, fields, true);
        fieldMapping[field.id] = copiedField;
        originalFields[field.id] = field;
      }
    } else {
      for (const field of clipboard.fields) {
        const copiedField = copyField(field, fields);
        fields.push(copiedField);
        fieldMapping[field.id] = copiedField;
        originalFields[field.id] = field;
      }
    }
  }

  if (currentTemplateView === 1 && clipboard.dataLayoutPositions) {
    const currentTabKeyPath = state.currentTabKeyPath;
    const currentTabSections = getIn<DataLayoutSection[]>(state, [...currentTabKeyPath, 'sections']);

    if (currentTabSections) {
      let currentSectionPositionsKeyPath: KeyPath;
      const selectedDataLayoutSectionKeyPath = state.selection.dataLayoutSections ? state.selection.dataLayoutSections[0] : null;
      const sourceTabKeyPath = getParentKeyPath(getParentKeyPath(clipboard.sourceSection));

      if (selectedDataLayoutSectionKeyPath) {
        currentSectionPositionsKeyPath = [...selectedDataLayoutSectionKeyPath, 'positions'];
      } else if (compareArrays(sourceTabKeyPath, currentTabKeyPath)) {
        currentSectionPositionsKeyPath = [...clipboard.sourceSection, 'positions'];
      } else {
        currentSectionPositionsKeyPath = [...currentTabKeyPath, 'sections', currentTabSections.length - 1, 'positions'];
      }

      const newPositionIds: string[] = [];
      const dataLayoutPositions = getIn<DataLayoutPosition[]>(state, currentSectionPositionsKeyPath);

      for (const dataLayoutPosition of clipboard.dataLayoutPositions) {
        if (dataLayoutPosition) {
          const copiedField = fieldMapping[dataLayoutPosition.fieldId];
          const originalField = originalFields[dataLayoutPosition.fieldId];

          if (copiedField) {
            const copiedDataLayoutPosition = copyDataLayoutPosition(dataLayoutPosition, copiedField, originalField);
            dataLayoutPositions.push(copiedDataLayoutPosition);
            newPositionIds.push(copiedDataLayoutPosition.id);
          }
        }
      }

      const sortedDataLayoutPositions = reorderItems(dataLayoutPositions);
      setIn(state, currentSectionPositionsKeyPath, sortedDataLayoutPositions);

      const newSelectedDataLayoutPositionKeyPaths: KeyPath[] = [];

      for (const dataLayoutPositionId of newPositionIds) {
        const index = dataLayoutPositions.findIndex((x) => x.id === dataLayoutPositionId);
        newSelectedDataLayoutPositionKeyPaths.push([...currentSectionPositionsKeyPath, ...[index]]);
      }

      setIn(state, ['selection', 'dataLayoutPositions'], [...newSelectedDataLayoutPositionKeyPaths]);
    }
  }

  const currentPageKeyPath = state.clipboard?.isVisible
    ? clipboard.sourcePage
    : ['template', 'pages', state.template.pages.findIndex((x) => x.id === state.currentPageId)];

  if (currentTemplateView === 0 && activeEditor === EditorTypes.FieldEditor && clipboard.fieldPositions) {
    const currentPageFieldPositionsKeyPath: KeyPath = [...currentPageKeyPath, ...['fieldPositions']];
    const newSelectedFieldPositionKeyPaths: KeyPath[] = [];
    const fieldPositions = getIn<FieldPosition[]>(state, currentPageFieldPositionsKeyPath);
    const pageWidth = getIn<number>(state, [...currentPageKeyPath, ...['width']]);
    const pageHeight = getIn<number>(state, [...currentPageKeyPath, ...['height']]);

    for (const fieldPosition of clipboard.fieldPositions) {
      if (fieldPosition) {
        const copiedField = fieldMapping[fieldPosition.fieldId];
        const field = clipboard.fields.find((x) => x.id === fieldPosition.fieldId);

        if (field && copiedField) {
          const copiedFieldPosition = copyFieldPosition(fieldPosition, field, copiedField, pageWidth, pageHeight, fieldPositions);
          const length = fieldPositions.push(copiedFieldPosition);
          // the new position is the end of the list
          newSelectedFieldPositionKeyPaths.push([...currentPageFieldPositionsKeyPath, length - 1]);
        }
      }
    }

    setIn(state, currentPageFieldPositionsKeyPath, fieldPositions);
    updateSelectedFieldPositions(state, {
      fieldPositionKeyPaths: newSelectedFieldPositionKeyPaths
    });
  }
  state.template.fields = fields as TemplateField[];

  if (currentTemplateView === 0 && activeEditor === EditorTypes.PageEditor && clipboard.elements) {
    //reset the selection state for elements. Each add will auto select the new element. then at the end we will add each one to the selection pool
    state.selection.elements = [];
    const selectedElements = [];
    currentTemplateView === 0 &&
      clipboard.elements?.forEach((element) => {
        copyBasicElementPosition(element, currentPageKeyPath, state, DEFAULT_PASTE_OFFSET);
        selectedElements.push(...state.selection.elements);
      });
    state.selection.elements = selectedElements;
  }
  return state;
}

export function duplicateSelectedItems(state: TemplateState, clipboard: TemplateClipboard): TemplateState {
  return pasteSelectedItems(state, clipboard);
}

export function replicateSelectedItems(state: TemplateState, clipboard: TemplateClipboard): TemplateState {
  return pasteSelectedItems(state, clipboard, true);
}

export function updateName(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { name } = payload;

  if (!!name) {
    state.template = { ...state.template, name };
  } else {
    state.errorMsg = {
      title: 'Name Required',
      message: 'A name is required. Please enter a name.'
    };
  }

  return state;
}

export function updateIdentifierFieldId(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldKeyPath } = payload;

  const fieldId = !!fieldKeyPath ? getIn<string>(state, [...fieldKeyPath, 'id']) : null;

  state.template.identifierFieldId = fieldId;

  return state;
}

export function updateFieldName(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPath } = payload;
  const value = payload.value as string;

  if (!value) {
    return setNameRequiredError(state);
  }

  const fieldId = getIn<string>(state, [...getParentKeyPath(keyPath), 'id']);
  const isDuplicateName = state.template?.fields.some(({ id, name }) => name.toLowerCase() === value.toLowerCase() && id !== fieldId) ?? false;

  if (isDuplicateName) {
    return setDuplicateNameError(state);
  }

  updateFieldNameInValidationFormula(state, keyPath, value);
  setIn(state, keyPath, value);

  return state;
}

export function updateFieldNameInValidationFormula(state: TemplateState, keyPath: KeyPath, newFieldName: string) {
  // Update formula of field validation settings
  const fieldId = getIn<string>(state, [...getParentKeyPath(keyPath), 'id']);
  const field = getField(state, fieldId);
  const fieldValidationSettings = field.validationSettings ?? [];

  if (fieldValidationSettings.length == 0) {
    return;
  }

  fieldValidationSettings.forEach((validationSetting) => {
    const formula = mapCriteriaToFormula(validationSetting.criteriaType, newFieldName, validationSetting.inputValues);
    // Update formula for first (and only, for now) validation setting
    const formulaKeyPath = [...getParentKeyPath(keyPath), 'validationSettings', 0, 'formulas', 0];
    setIn(state, formulaKeyPath, formula);
  });

  return state;
}

export function updateFieldNameInputVisited(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPath } = payload;

  const templateField = getIn<TemplateField>(state, getParentKeyPath(keyPath));
  if (templateField) {
    setIn(state, keyPath, true);
  }

  return state;
}

// todo - for now, keep things separated, but consider refactoring the logic of this and updateFieldName
export function updateTableColumnName(state: TemplateState, payload: TemplateEditorPayload) {
  const { keyPath, value } = payload;

  if (!value) {
    return setNameRequiredError(state);
  }

  const loweredName = (value as string).toLowerCase();
  // get the id of the column
  const columnId = getIn<string>(state, [...getParentKeyPath(keyPath), 'id']);
  // at this point we know that the key path is the name property within one of the columns
  // get all of the columns in the same table by look 2 levels up
  const columns = getIn<TemplateColumn[]>(state, getParentKeyPathByLevel(keyPath, 2));
  // check for dup names
  const hasDuplicateName = columns.some(({ id, name }) => id !== columnId && name.toLowerCase() === loweredName);

  if (hasDuplicateName) {
    return setDuplicateNameError(state);
  }

  setIn(state, keyPath, value);

  return state;
}

export function updateFieldRowCount(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, rowCount } = payload;

  for (const keyPath of keyPaths) {
    const field = getIn<TemplateField>(state, keyPath);
    const previousRowCount = field.rowCount;

    // set new row count
    setIn(state, [...keyPath, 'rowCount'], rowCount);

    // update rows for each fieldPosition
    const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, field.id);

    for (const fieldPositionKeyPath of fieldPositionKeyPaths) {
      const fieldPosition = getIn<FieldPosition>(state, fieldPositionKeyPath);

      let numberOfRows = fieldPosition.numberOfRows;
      let startingRow = fieldPosition.startingRow;

      // Keep the FieldPosition showing all rows if it was previously
      if (numberOfRows === previousRowCount && startingRow === 1) {
        numberOfRows = rowCount;
      } else {
        numberOfRows = Math.min(numberOfRows, rowCount);

        if (startingRow + (numberOfRows - 1) > rowCount) {
          startingRow = Math.max(1, rowCount - (numberOfRows - 1));
        }
      }

      fieldPosition.startingRow = startingRow;
      updateNumberOfRows(state, fieldPositionKeyPath, numberOfRows);
    }
  }

  return state;
}

export function updateFieldDataSource(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, value, dataSourceOrigin } = payload;

  for (const keyPath of keyPaths) {
    setIn(state, [...keyPath, 'dataSource'], value);
    setIn(state, [...keyPath, 'displayColumn'], '');
    setIn(state, [...keyPath, 'dataSourceOrigin'], dataSourceOrigin);
  }

  return state;
}

export function updateFieldType(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths } = payload;
  const value = payload.value as number;

  for (const keyPath of keyPaths) {
    const field = getIn<TemplateField>(state, keyPath);

    if (value === FieldTypes.eSignature || value === FieldTypes.eSignatureInitials) {
      const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, field.id);

      for (const fieldPositionKeyPath of fieldPositionKeyPaths) {
        const fieldPosition = getIn<FieldPosition>(state, fieldPositionKeyPath);

        // change aspect ratio
        const aspectRatio = value === FieldTypes.eSignature ? SignatureAspectRatio : InitialsAspectRatio;
        const height = fieldPosition.h;
        setIn(state, [...fieldPositionKeyPath, 'w'], Math.round(height * aspectRatio));
      }
    }

    setIn(state, [...keyPath, 'type'], value);

    removeTemplateValidations(state, { fieldId: field.id });
  }

  return state;
}

export function updateItemCollectionsResetDirty(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  // look at the item collections from the payload
  // find the matching collection in the state
  // set `isDirty` to `false`
  const itemsToReset = payload.itemCollections?.map((i) => i.id) ?? [];

  if (itemsToReset.length === 0) {
    return state;
  }

  itemsToReset.forEach((id) => {
    const item = state.itemCollections?.find((item) => item.id === id);

    if (!!item) {
      item.isDirty = false;
    }
  });

  return state;
}

export function updateFieldDropDownItems(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, items, itemCollectionTemplateId, itemCollection } = payload;

  for (const keyPath of keyPaths) {
    if (itemCollectionTemplateId) {
      setIn(state, [...keyPath, ...['itemCollectionTemplateId']], itemCollectionTemplateId);
      setIn(state, [...keyPath, ...['items']], null);
    } else {
      setIn(state, [...keyPath, ...['itemCollectionTemplateId']], null);
      setIn(state, [...keyPath, ...['items']], items);
    }
  }

  if (itemCollectionTemplateId && itemCollection) {
    let itemCollections = state.itemCollections;
    const itemCollectionIndex = itemCollections.findIndex((x) => x.id === itemCollectionTemplateId);

    if (itemCollectionIndex > -1) {
      itemCollections[itemCollectionIndex] = itemCollection;
    } else {
      itemCollections = insertAt(itemCollections, 0, itemCollection);
    }

    state.itemCollections = itemCollections;
  }

  return state;
}

export function updateFieldCheckBoxGroupItems(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths } = payload;
  const value = payload.value as Identifiable[];

  for (const keyPath of keyPaths) {
    const removedItems: Identifiable[] = [];
    const oldItems = getIn<Identifiable[]>(state, keyPath);

    setIn(state, keyPath, value);

    if (oldItems) {
      for (const oldItem of oldItems) {
        const newItemIndex = value.findIndex((x) => x.id === oldItem.id);

        if (newItemIndex === -1) {
          // item was deleted
          removedItems.push(oldItem);
        }
      }
    }

    for (const removedItem of removedItems) {
      // delete any field positions that are linked to this item
      const fieldPositionKeyPaths = getFieldPositionKeyPathsByCheckBoxItemId(state, removedItem.id);
      state = deleteItems(state, fieldPositionKeyPaths);

      // update datalayout positions that are linked to this item
      const dataLayoutPositionItemKeyPaths = getDataLayoutPositionItemKeyPathsByCheckBoxItemId(state, removedItem.id);
      state = deleteItems(state, dataLayoutPositionItemKeyPaths);

      // delete any datalayout position that doesn't reference an item
      const dataLayoutPositionKeyPathsToDelete: KeyPath[] = [];

      for (const dataLayoutPositionItemKeyPath of dataLayoutPositionItemKeyPaths) {
        const itemsKeyPath = getParentKeyPath(dataLayoutPositionItemKeyPath);
        const dataLayoutPositionItems = getIn<DataLayoutPosition[]>(state, itemsKeyPath);

        if (dataLayoutPositionItems.length === 0) {
          // position is not referencing any items so we should delete it
          dataLayoutPositionKeyPathsToDelete.push(getParentKeyPath(itemsKeyPath));
        }
      }

      state = deleteItems(state, dataLayoutPositionKeyPathsToDelete);
    }
  }

  return state;
}

export function updateValidation(state: TemplateState, keypaths: KeyPath[], value: string): TemplateState {
  for (const keypath of keypaths) {
    setIn(state, keypath, value);
  }

  return state;
}

export function updateFormula(state: TemplateState, keyPaths: KeyPath[], formulaKey: string, formula: TypeMePlease): TemplateState {
  for (const keyPath of keyPaths) {
    const formulasKeyPath = [...keyPath, 'formulas'];
    let formulas = getIn<TemplateFormula[]>(state, formulasKeyPath) ?? [];
    let formulaIndex = formulas.findIndex((f) => f.key === formulaKey);

    if (formula) {
      // create or update formula
      if (formulaIndex === -1) {
        formulaIndex = formulas ? formulas.length : 0;
      }

      formulas[formulaIndex] = {
        key: formulaKey,
        value: formula
      };
    } else if (formulaIndex > -1) {
      // delete formula
      formulas = formulas.filter((_, idx) => idx !== formulaIndex);
    }

    setIn(state, formulasKeyPath, formulas);
  }

  return state;
}

export function updateDefaultFieldPositionSettings(state: TemplateState, fieldType: number, name: string, value: unknown): TemplateState {
  return updateDefaultFieldSettings(state, fieldType, name, value);
}

export function updateDefaultFieldSettings(state: TemplateState, fieldType: number, name: string, value: unknown): TemplateState {
  setIn(state, ['defaultSettings', getDefaultSettingType(fieldType), name], value);

  return state;
}

export function updateFieldProperty(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, value, formulaKey } = payload;
  state = updateProperty(state, payload);

  // clear formulas
  if (formulaKey) {
    state = updateFormula(state, getParentKeyPaths(keyPaths), formulaKey, null);
  }

  // set default settings
  for (const keyPath of keyPaths) {
    const propertyName = keyPath[keyPath.length - 1] as string;

    if (['maxFileSize', 'maxFileCount', 'allowedFileTypes'].includes(propertyName)) {
      const fieldId = getIn<string>(state, [...getParentKeyPath(keyPath), 'id']);
      const field = getField(state, fieldId);

      state = updateDefaultFieldSettings(state, field.type, propertyName, value);
    }
  }

  return state;
}

export function updateFieldPositionProperty(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, value, formulaKey } = payload;
  state = updateProperty(state, payload);

  // clear formulas
  if (formulaKey) {
    state = updateFormula(state, getParentKeyPaths(keyPaths), formulaKey, null);
  }

  // set default settings
  for (const keyPath of keyPaths) {
    const propertyName = keyPath[keyPath.length - 1] as string;

    if (['backgroundColor', 'focusColor', 'color', 'fontName', 'fontSize', 'align'].includes(propertyName)) {
      const fieldId = getIn<string>(state, [...getParentKeyPath(keyPath), 'fieldId']);
      const field = getField(state, fieldId);

      updateFieldPositionsToUseCustomFieldPositionSettings(state);

      state = updateDefaultFieldPositionSettings(state, field.type, propertyName, value);
    }
  }

  return state;
}

export function updateFieldPositionLineHeight(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths } = payload;
  const value = payload.value as number;

  for (const keyPath of keyPaths) {
    const fontSize = getIn<number>(state, [...getParentKeyPath(keyPath), 'fontSize']);
    const lineHeight = value || value === 0 ? Math.max(value, fontSize + 2) : null;

    setIn(state, keyPath, lineHeight);
  }

  return state;
}

export function updateFieldPositionFontSize(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths } = payload;
  const value = payload.value as number;

  for (const keyPath of keyPaths) {
    setIn(state, keyPath, value);

    const lineHeightKeyPath = [...getParentKeyPath(keyPath), 'lineHeight'];
    const lineHeight = getIn<number>(state, lineHeightKeyPath);

    if (+lineHeight < value + 2) {
      setIn(state, lineHeightKeyPath, value + 2);
    }
  }

  updateFieldPositionsToUseCustomFieldPositionSettings(state);

  return state;
}

export function updateFieldPositionDisplayType(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths } = payload;
  const displayType = payload.value as number;

  for (const keyPath of keyPaths) {
    const fieldPositionKeyPath = getParentKeyPath(keyPath);
    const fieldPosition = getIn<FieldPosition>(state, fieldPositionKeyPath);
    const field = getField(state, fieldPosition.fieldId);
    const fieldType = field.type;

    if (fieldType === FieldTypes.CheckBox || fieldType === FieldTypes.CheckBoxGroup) {
      const previousDisplayType = getIn<number>(state, keyPath);

      if (
        (previousDisplayType === DisplayType.Oval || previousDisplayType === DisplayType.FilledOval) &&
        displayType !== DisplayType.Oval &&
        displayType !== DisplayType.FilledOval
      ) {
        // make the position square
        const size = Math.min(fieldPosition.w, fieldPosition.h);

        setIn(state, [...fieldPositionKeyPath, 'w'], size);
        setIn(state, [...fieldPositionKeyPath, 'h'], size);
      }
    }

    state = updateDefaultFieldPositionSettings(state, field.type, 'displayType', displayType);
    setIn(state, keyPath, displayType);
  }

  return state;
}

export function updateFieldPositionStartingRow(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, value } = payload;

  for (const keyPath of keyPaths) {
    const fieldPositionKeyPath = getParentKeyPath(keyPath);
    const fieldPosition = getIn<FieldPosition>(state, fieldPositionKeyPath);
    const field = getField(state, fieldPosition.fieldId);
    const rowCount = field.rowCount;

    const startingRow = Math.min(Math.max(1, value as number), rowCount);
    setIn(state, keyPath, startingRow);

    let numberOfRows = fieldPosition.numberOfRows;

    if (startingRow + (numberOfRows - 1) > rowCount) {
      numberOfRows = Math.max(1, rowCount - (startingRow - 1));
      updateNumberOfRows(state, fieldPositionKeyPath, numberOfRows);
    }
  }

  return state;
}

export function updateFieldPositionNumberOfRows(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, value } = payload;

  for (const keyPath of keyPaths) {
    const fieldPositionKeyPath = getParentKeyPath(keyPath);
    const fieldPosition = getIn<FieldPosition>(state, fieldPositionKeyPath);
    const field = getField(state, fieldPosition.fieldId);
    const rowCount = field.rowCount;
    const startingRow = fieldPosition.startingRow;

    let numberOfRows = Math.max(1, value as number);

    if (startingRow + (numberOfRows - 1) > rowCount) {
      numberOfRows = Math.max(1, rowCount - (startingRow - 1));
    }

    updateNumberOfRows(state, fieldPositionKeyPath, numberOfRows);
  }

  return state;
}

export function addBasicElementsActions(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, elementAction } = payload;

  state.elementActions ??= [];
  state.elementActions.push(
    ...keyPaths
      .map((keyPath) => getIn<BasicElement>(state, keyPath))
      .filter((element) => element.isLocked == null || element.isLocked === false)
      .map((element) => {
        return {
          element: element,
          ...elementAction
        };
      })
  );

  return state;
}

export function updateBasicElementsGroupAlign(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, elementAction } = payload;

  const elementsToUpdate = keyPaths.map((elementsKeyPath) => getIn<BasicElement>(state, elementsKeyPath));

  const getTrueWidth = (element: BasicElement) => (element.scaleX || 1) * element.width;
  const getTrueHeight = (element: BasicElement) => (element.scaleY || 1) * element.height;

  const updateHandlers = {
    [BasicElementGroupAlignAction.GroupAlignLeft]: (elements: BasicElement[]) => {
      const furthestLeftCoordinate = Math.min(...elements.map((element) => element.x));
      return elements.map((element) => {
        return {
          id: element.id,
          x: furthestLeftCoordinate
        };
      });
    },
    [BasicElementGroupAlignAction.GroupAlignMiddle]: (elements: BasicElement[]) => {
      const furthestLeftCoordinate = Math.min(...elements.map((element) => element.x));
      const furthestRightCoordinate = Math.max(...elements.map((element) => element.x + getTrueWidth(element)));
      const totalWidth = furthestRightCoordinate - furthestLeftCoordinate;
      const centerCoordinate = furthestLeftCoordinate + totalWidth / 2;
      return elements.map((element) => {
        const delta = centerCoordinate - (element.x + getTrueWidth(element) / 2);
        return {
          id: element.id,
          x: element.x + delta
        };
      });
    },
    [BasicElementGroupAlignAction.GroupAlignRight]: (elements: BasicElement[]) => {
      const furthestRightCoordinate = Math.max(...elements.map((element) => element.x + getTrueWidth(element)));
      return elements.map((element) => {
        return {
          id: element.id,
          x: furthestRightCoordinate - getTrueWidth(element)
        };
      });
    },
    [BasicElementGroupAlignAction.GroupAlignTop]: (elements: BasicElement[]) => {
      const furthestTopCoordinate = Math.min(...elements.map((element) => element.y));
      return elements.map((element) => {
        return {
          id: element.id,
          y: furthestTopCoordinate
        };
      });
    },
    [BasicElementGroupAlignAction.GroupAlignCenter]: (elements: BasicElement[]) => {
      const furthestTopCoordinate = Math.min(...elements.map((element) => element.y));
      const furthestBottomCoordinate = Math.max(...elements.map((element) => element.y + getTrueHeight(element)));
      const totalHeight = furthestBottomCoordinate - furthestTopCoordinate;
      const centerCoordinate = furthestTopCoordinate + totalHeight / 2;
      return elements.map((element) => {
        const delta = centerCoordinate - (element.y + getTrueHeight(element) / 2);
        return {
          id: element.id,
          y: element.y + delta
        };
      });
    },
    [BasicElementGroupAlignAction.GroupAlignBottom]: (elements: BasicElement[]) => {
      const furthestBottomCoordinate = Math.max(...elements.map((element) => element.y + getTrueHeight(element)));
      return elements.map((element) => {
        return {
          id: element.id,
          y: furthestBottomCoordinate - getTrueHeight(element)
        };
      });
    }
  };

  return updateBasicElementsProperties(state, { keyPaths: keyPaths, elements: updateHandlers[elementAction.value](elementsToUpdate) });
}

export function updateBasicElementsProperties(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, element, elements, fileSrc, forceUpdate } = payload;

  //there are few computed properties that need to be handled
  const sanitizeUpdate = (update: BasicElement, currentElement: BasicElement) => {
    const newUpdate = { ...update };
    const computedWidth = newUpdate[BasicElementComputedProperties.Width];
    delete newUpdate[BasicElementComputedProperties.Width];
    if (computedWidth) {
      newUpdate.scaleX = !currentElement.width ? 0 : computedWidth / currentElement.width;
    }

    const computedHeight = newUpdate[BasicElementComputedProperties.Height];
    delete newUpdate[BasicElementComputedProperties.Height];
    if (computedHeight) {
      newUpdate.scaleY = !currentElement.height ? 0 : computedHeight / currentElement.height;
    }

    return newUpdate;
  };

  for (const elementsKeyPath of keyPaths) {
    const currentElement = getIn<BasicElement>(state, elementsKeyPath);
    const update = element || elements?.find((e) => e.id === currentElement.id);

    //make sure to not update something without a match
    if (!update) {
      continue;
    }
    // currentElement can be undefined if it was the only element on the canvas and was deleted
    const canUpdate = forceUpdate || update.isLocked === false || (currentElement && (currentElement.isLocked == null || currentElement.isLocked === false));
    if (!canUpdate) {
      continue;
    }

    const newElement = {
      ...currentElement,
      ...sanitizeUpdate(update, currentElement)
    };

    if (fileSrc) {
      const existingFile = state.files?.find((file) => file.src === fileSrc);
      if (existingFile) {
        //I refuse to be tricked....you cant upload the same image and think Im gonna upload it!
        newElement.fileId = existingFile.id;
      } else {
        newElement.fileId = getGuid();
        //add the element to the uploads
        addBasicElementUpload(state, {
          elementUpload: {
            fileSrc: fileSrc,
            fileId: newElement.fileId
          }
        });

        //also have to add the upload to the current files list
        addTemplateFile(state, {
          id: newElement.fileId,
          fileSrc: fileSrc,
          fileName: newElement.fileName,
          //currently the only "files" that are supported in uploads are images
          templateFileType: newElement.type === BasicElementTypes.Image ? TemplateFileTypes.Image : null
        });
      }
    }

    setIn(state, elementsKeyPath, newElement);
  }

  return state;
}

export function updateBasicElementsZIndex(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { pageKeyPath, ids } = payload;

  const elementsKeyPath = [...pageKeyPath, 'elements'];
  const currentElements = getIn<BasicElement[]>(state, elementsKeyPath) ?? [];
  const idMap = Object.fromEntries(currentElements.map((currentElement) => [currentElement.id, currentElement]));

  const selectedElements = state.selection?.elements?.map((selectedElementKeyPath) => getIn<BasicElement>(state, selectedElementKeyPath)) ?? [];

  const newElements = ids.map((id) => idMap[id]).filter((element) => element != null);

  setIn(state, elementsKeyPath, newElements);

  //have to update the selection because we are adjusting the state array so the keypaths have changed
  const newSelectedElementIndexes = selectedElements
    .map((selectedElement) => newElements.findIndex((newElement) => selectedElement?.id === newElement.id))
    .filter((index) => index != -1)
    .map((index) => [...pageKeyPath, 'elements', index]);

  //clear out element actions for z index that had matches to this update
  state.elementActions = state.elementActions.filter(
    (elementAction) => elementAction.type !== BasicElementActionTypes.ZIndex || !ids.some((id) => id === elementAction.element?.id)
  );
  state.selection = { elements: newSelectedElementIndexes };
  state.selection.boundingRectangle = null;
  return state;
}

export function addTemplateFile(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fileSrc, fileName, id, templateFileType } = payload;
  const files = state.files || [];
  const existingFile = files.find((file) => id === file.id);
  if (existingFile) {
    //mutate the state array by removing the existing version of the file
    files.splice(files.indexOf(existingFile), 1);
  }

  const newFile = {
    id: id,
    name: fileName,
    type: templateFileType,
    src: fileSrc
  };

  //add the upload to the end
  files.push(newFile);
  state.files = files;
  return state;
}

export function addBasicElementUpload(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { elementUpload } = payload;
  const elementUploads = state.elementUploads || [];

  const exitingElementUpload = elementUploads.find((statePage) => {
    return elementUpload.pageId ? statePage.pageId === elementUpload.pageId : elementUpload.fileId ? statePage.fileId === elementUpload.fileId : null;
  });

  if (exitingElementUpload) {
    //mutate the state array by removing the existing version of the upload
    elementUploads.splice(elementUploads.indexOf(exitingElementUpload), 1);
  }

  //add the upload to the end
  elementUploads.push({ ...(exitingElementUpload || {}), ...elementUpload });
  state.elementUploads = elementUploads;

  return state;
}

export function addDraftPageThumbnail(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { pageThumbnail } = payload;
  const draftPageThumbnails = state.draftPageThumbnails || [];

  if (pageThumbnail.pageId == null) {
    return state;
  }

  const existingThumnail = draftPageThumbnails.find((statePage) => {
    return statePage.pageId === pageThumbnail.pageId;
  });

  if (existingThumnail?.dataUrl === pageThumbnail.dataUrl) {
    return state;
  }

  if (existingThumnail) {
    //mutate the state array by removing the existing version of the upload
    draftPageThumbnails.splice(draftPageThumbnails.indexOf(existingThumnail), 1);
  }

  //add the upload to the end
  draftPageThumbnails.push({ ...(existingThumnail || {}), ...pageThumbnail });
  state.draftPageThumbnails = draftPageThumbnails;

  return state;
}

export function resetBasicElementUploads(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { template } = payload;
  state.elementUploads = [];

  //optional payload that will add updated uploads to the the template state
  const pages =
    template?.pages?.map((page) => {
      return {
        id: page.id,
        fileId: page.fileId,
        originalPdfFileId: page.originalPdfFileId
      };
    }) || [];

  for (const page of pages) {
    const matchingPage = state.template.pages.find((p) => p.id === page.id);
    if (matchingPage == null) {
      continue;
    }

    matchingPage.originalPdfFileId = page.originalPdfFileId;
    matchingPage.fileId = page.fileId;
  }

  return state;
}

export function updateTemplateAddView(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { viewType } = payload;

  switch (viewType) {
    case 'listView':
      state.template.dataLayouts = [
        {
          id: getGuid(),
          dataLayoutTabs: [
            {
              id: getGuid(),
              name: 'Tab 1',
              sortNumber: 0,
              sections: [
                {
                  id: getGuid(),
                  sortNumber: 0
                }
              ]
            }
          ]
        }
      ];

      //If the user is going to list view then we do not want to allow the page editor controls to be active
      state.activeEditor = EditorTypes.FieldEditor;

      break;
    case 'formView':
      state.addingFormView = true;

      break;
    default:
      break;
  }

  return state;
}

export function clearSelection(state: TemplateState): TemplateState {
  state.selection = {};

  return state;
}

export function updateTemplateRemoveView(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { viewType } = payload;

  clearSelection(state);

  switch (viewType) {
    case 'listView':
      state.template.dataLayouts = [];

      break;
    case 'formView':
      state.template.pages = [];
      state.addingFormView = false;

      break;
    default:
      break;
  }

  return state;
}

export function updateColumnPositionDisplayType(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths } = payload;
  const displayType = payload.value;

  for (const keyPath of keyPaths) {
    const fieldPositionKeyPath = getParentKeyPath(getParentKeyPath(getParentKeyPath(keyPath)));
    const fieldPosition = getIn<FieldPosition>(state, fieldPositionKeyPath);
    const columnPositionKeyPath = getParentKeyPath(keyPath);
    const columnPosition = getIn<TableColumnPosition>(state, columnPositionKeyPath);
    const column = getColumn(state, fieldPosition.fieldId, columnPosition.columnId);

    state = updateDefaultFieldPositionSettings(state, column.type, 'displayType', displayType);
    setIn(state, keyPath, displayType);
  }

  return state;
}

export function updateFieldPositionCoordinates(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldPositionKeyPaths, coordinates, resizeProportially } = payload;

  let offset = payload.offset;

  for (const fieldPositionKeyPath of fieldPositionKeyPaths) {
    const fieldPosition = deepClone(getIn<FieldPosition>(state, fieldPositionKeyPath));
    const field = getField(state, fieldPosition.fieldId);

    if (coordinates) {
      if (coordinates.x !== undefined) {
        offset = { x: coordinates.x - fieldPosition.x };
      }

      if (coordinates.y !== undefined) {
        offset = { y: coordinates.y - fieldPosition.y };
      }

      if (coordinates.w !== undefined) {
        offset = { w: coordinates.w - fieldPosition.w };
      }

      if (coordinates.h !== undefined) {
        offset = { h: coordinates.h - fieldPosition.h };
      }
    }

    const keepAspectRatio =
      field.type === FieldTypes.eSignature ||
      field.type === FieldTypes.eSignatureInitials ||
      ((field.type === FieldTypes.CheckBox || field.type === FieldTypes.CheckBoxGroup) &&
        fieldPosition.displayType !== DisplayType.Oval &&
        fieldPosition.displayType !== DisplayType.FilledOval);

    if (keepAspectRatio) {
      let aspectRatio;

      if (field.type === FieldTypes.CheckBox || field.type === FieldTypes.CheckBoxGroup) {
        aspectRatio = 1;
      } else if (field.type === FieldTypes.eSignature || field.type === FieldTypes.eSignatureInitials) {
        aspectRatio = field.type === FieldTypes.eSignature ? SignatureAspectRatio : InitialsAspectRatio;
      }

      if (offset.w && offset.h) {
        if (offset.w < offset.h * aspectRatio) {
          offset.h = Math.round(offset.w / aspectRatio);
        }

        if (offset.h < offset.w / aspectRatio) {
          offset.w = Math.round(offset.h * aspectRatio);
        }
      } else if (offset.w) {
        offset.h = Math.round(offset.w / aspectRatio);
      } else if (offset.h) {
        offset.w = Math.round(offset.h * aspectRatio);
      }
    }

    if (offset.x !== undefined) {
      setIn(state, [...fieldPositionKeyPath, ...['x']], Math.round(fieldPosition.x + offset.x));
    }

    if (offset.y !== undefined) {
      setIn(state, [...fieldPositionKeyPath, ...['y']], Math.round(fieldPosition.y + offset.y));
    }

    if (offset.w !== undefined) {
      const columns = fieldPosition.columns ? fieldPosition.columns.filter((col) => !col.hidden) : null;

      if (columns && columns.length > 0) {
        // update all columns proportionally
        // TODO these weren't really sorted since sortNumber doesn't exist here
        const sortedColumns = columns; //.sort((a, b) => a.sortNumber - b.sortNumber);
        const columnWidths: number[] = [];

        for (const column of sortedColumns) {
          columnWidths.push(column.w);
        }

        const newColumnWidths = adjustRowHeightsByOffset(columnWidths, offset.w, resizeProportially);

        for (let columnNumber = 0; columnNumber < sortedColumns.length; columnNumber++) {
          const column = sortedColumns[columnNumber];
          const columnIndex = columns.findIndex((x) => x.columnId === column.columnId);
          const columnWidthKeyPath = [...fieldPositionKeyPath, 'columns', columnIndex, 'w'];

          setIn(state, columnWidthKeyPath, Math.round(newColumnWidths[columnNumber]));
        }

        state = updateTableFieldPositionWidth(state, fieldPositionKeyPath);
      }

      const w = Math.max(2, Math.round(fieldPosition.w + offset.w));
      setIn(state, [...fieldPositionKeyPath, 'w'], w);
      state = updateDefaultFieldPositionSettings(state, field.type, 'w', w);
    }

    if (offset.h !== undefined) {
      const rows = fieldPosition.rows;

      if (rows && rows.length > 0) {
        // update all rows proportionally
        const sortedRows = rows.sort((a, b) => a.rowNumber - b.rowNumber);
        const rowHeights: number[] = sortedRows.map((row) => row.h);
        const newRowHeights = adjustRowHeightsByOffset(rowHeights, offset.h, resizeProportially);

        for (let rowNumber = 0; rowNumber < sortedRows.length; rowNumber++) {
          const row = sortedRows[rowNumber];
          const rowIndex = rows.findIndex((x) => x.rowNumber === row.rowNumber);
          const rowHeightKeyPath = [...fieldPositionKeyPath, 'rows', rowIndex, 'h'];

          setIn(state, rowHeightKeyPath, Math.round(newRowHeights[rowNumber]));
        }

        const fieldRows = getIn<TableRowPosition[]>(state, [...fieldPositionKeyPath, 'rows']);
        const fieldHeight = fieldRows.map((fr) => fr.h).reduce((prev, curr) => prev + curr, 0);

        setIn(state, [...fieldPositionKeyPath, 'h'], fieldHeight);
        state = updateDefaultFieldPositionSettings(state, field.type, 'h', fieldHeight);
      } else {
        const h = Math.max(2, Math.round(fieldPosition.h + offset.h));
        setIn(state, [...fieldPositionKeyPath, 'h'], h);
        state = updateDefaultFieldPositionSettings(state, field.type, 'h', h);
      }
    }
  }

  return state;
}

export function updateSelectedObjectsCoordinates(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  return updateSelectedBasicElementsCoordinates(updateSelectedFieldPositionCoordinates(state, payload), payload);
}

export function updateSelectedFieldPositionCoordinates(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { offset, resizeProportially } = payload;
  const selectedFieldPositionKeyPaths = state.selection.fieldPositions;
  const boundingRectangle = state.selection.boundingRectangle;

  if (!boundingRectangle) {
    return state;
  }

  if (selectedFieldPositionKeyPaths?.length) {
    const pageKeyPath = getParentKeyPath(getParentKeyPath(selectedFieldPositionKeyPaths[0]));
    if (offset.x && offset.x < 0) {
      // moving left
      offset.x = Math.max(-boundingRectangle.left, offset.x);
    } else if (offset.y && offset.y < 0) {
      // moving up
      offset.y = Math.max(-boundingRectangle.top, offset.y);
    } else if (offset.x && offset.x > 0) {
      // moving right
      const pageWidth = getIn<number>(state, [...pageKeyPath, 'width']);
      offset.x = Math.min(pageWidth - boundingRectangle.right, offset.x);
    } else if (offset.y && offset.y > 0) {
      // moving down
      const pageHeight = getIn<number>(state, [...pageKeyPath, 'height']);
      offset.y = Math.min(pageHeight - boundingRectangle.bottom, offset.y);
    }

    for (const fieldPositionKeyPath of selectedFieldPositionKeyPaths) {
      state = updateFieldPositionCoordinates(state, { fieldPositionKeyPaths: [fieldPositionKeyPath], offset, resizeProportially });
    }
  }

  return state;
}

export function updateSelectedBasicElementsCoordinates(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { offset } = payload;
  const selectedElementKeyPaths = state.selection.elements;
  const boundingRectangle = state.selection.boundingRectangle;

  if (!boundingRectangle) {
    return state;
  }

  if (selectedElementKeyPaths?.length) {
    const elements = selectedElementKeyPaths.map((selectedElementKeyPath) => {
      const element = getIn<BasicElement>(state, selectedElementKeyPath);
      if (!element.isLocked) {
        element.x += offset.x || 0;
        element.y += offset.y || 0;
      }
      return element;
    });

    state = updateBasicElementsProperties(state, {
      keyPaths: selectedElementKeyPaths,
      elements: elements
    });
  }

  return state;
}

export function updatedSelectionBoundingRectangle(state: TemplateState): TemplateState {
  const selectedFieldPositions = getSelectedFieldPositions(state);
  const selectedElements = getSelecteBasicElements(state);
  const boundingRectangle = getBoundingRectangle(selectedFieldPositions, selectedElements);

  state.selection.boundingRectangle = boundingRectangle;

  return state;
}

export function updateSelectedFields(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldIds, append } = payload;
  const newFieldIds = [...fieldIds];

  if (append) {
    const currentSelection = state.selection;

    if (currentSelection) {
      newFieldIds.push(...(currentSelection.fieldIds ?? []));
    }
  }

  state.selection = { fieldIds: newFieldIds };

  return state;
}

export function navigateToSelectedField(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldIds, append } = payload;
  const newFieldIds = [...fieldIds];

  if (append) {
    const currentSelection = state.selection;

    if (currentSelection) {
      newFieldIds.push(...(currentSelection.fieldIds ?? []));
    }
  }

  const formView = state.currentTemplateView === 0 ? true : false;
  if (formView) {
    const fieldPositionKeyPaths: KeyPath[] = state.selection?.fieldPositions;
    state.selection = { fieldPositions: fieldPositionKeyPaths, fieldIds: newFieldIds };
  } else {
    const dataLayoutPositionKeyPaths: KeyPath[] = state.selection?.dataLayoutPositions;
    state.selection = { dataLayoutPositions: dataLayoutPositionKeyPaths, fieldIds: newFieldIds };
  }

  return state;
}

export function updateSelectedFieldPositions(state: TemplateState, payload: Partial<TemplateEditorPayload>): TemplateState {
  const { fieldPositionKeyPaths, append } = payload;
  const newFieldPositionKeyPaths = [...fieldPositionKeyPaths];
  const selectedPageIndex = fieldPositionKeyPaths?.length > 0 ? fieldPositionKeyPaths[0][fieldPositionKeyPaths[0].length - 3] : null;

  if (append) {
    // check for field postion instead of selection since clicking on
    // the page (canvas) will result in a undefined postions
    const currentFieldPosition = state.selection?.fieldPositions;
    if (currentFieldPosition) {
      // only select fields on the same page
      for (const keyPath of currentFieldPosition) {
        const pageIndex = keyPath[keyPath.length - 3];
        if (pageIndex === selectedPageIndex) {
          newFieldPositionKeyPaths.push(keyPath);
        }
      }
    }
  }

  //when selecting a field on the template we want to update the selection of the fieldIds to
  const fieldIds = (newFieldPositionKeyPaths || [])
    .map((fieldPositionKeyPath) => getIn<FieldPosition>(state, fieldPositionKeyPath)?.fieldId)
    .filter((fieldId) => !!fieldId);

  state.selection = { fieldPositions: newFieldPositionKeyPaths, fieldIds: fieldIds };

  return updatedSelectionBoundingRectangle(state);
}

export function updateSelectedPages(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { pageKeyPaths, append } = payload;

  let newPageKeyPaths = [...pageKeyPaths];

  if (append) {
    const currentSelectedPages = state.selection?.pages;

    if (currentSelectedPages) {
      newPageKeyPaths = [...newPageKeyPaths, ...currentSelectedPages];
    }
  }

  state.selection = { pages: newPageKeyPaths };

  return state;
}

export function deselectFieldPositions(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldPositionKeyPaths } = payload;

  let selectedFieldPositionsKeyPaths = [...state.selection.fieldPositions];

  if (selectedFieldPositionsKeyPaths) {
    for (const fieldPositionKeyPath of fieldPositionKeyPaths) {
      const matchingIndex = selectedFieldPositionsKeyPaths.findIndex((keyPath) => compareArrays(keyPath, fieldPositionKeyPath));

      if (matchingIndex > -1) {
        selectedFieldPositionsKeyPaths = selectedFieldPositionsKeyPaths.filter((_, idx) => idx != matchingIndex);
      }
    }

    state.selection = { fieldPositions: selectedFieldPositionsKeyPaths };

    return updatedSelectionBoundingRectangle(state);
  }

  return state;
}

export function selectAllFieldPositionsOnPage(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { pageId } = payload;
  const pages = state.template.pages;
  const pageIndex = getItemIndexId(pages, pageId);
  const fieldPositionsKeyPath: KeyPath = ['template', 'pages', pageIndex, 'fieldPositions'];
  const fieldPositions = getIn<FieldPosition[]>(state, fieldPositionsKeyPath);
  const fieldPositionKeyPaths: KeyPath[] = [];

  for (const fieldPosition of fieldPositions) {
    const fieldPositionIndex = fieldPositions.findIndex((x) => x.id === fieldPosition.id);
    fieldPositionKeyPaths.push([...fieldPositionsKeyPath, fieldPositionIndex]);
  }

  return updateSelectedFieldPositions(state, {
    fieldPositionKeyPaths,
    append: false
  });
}

export function updateSelectedFieldPositionsInRectangle(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { pageId, rect, append } = payload;

  const r1 = {
    left: rect.x,
    top: rect.y,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  };

  const pages = state.template.pages;
  const pageIndex = getItemIndexId(pages, pageId);
  const fieldPositionsKeyPath: KeyPath = ['template', 'pages', pageIndex, 'fieldPositions'];
  const fieldPositions = getIn<FieldPosition[]>(state, fieldPositionsKeyPath) ?? [];

  const filteredPositions = fieldPositions.filter((fieldPosition) => {
    const r2 = {
      left: fieldPosition.x,
      top: fieldPosition.y,
      right: fieldPosition.x + fieldPosition.w,
      bottom: fieldPosition.y + fieldPosition.h
    };

    return intersectRect(r1, r2);
  });

  const fieldPositionKeyPaths: KeyPath[] = [];

  for (const fieldPosition of filteredPositions) {
    const fieldPositionIndex = fieldPositions.findIndex((x) => x.id === fieldPosition.id);
    fieldPositionKeyPaths.push([...fieldPositionsKeyPath, fieldPositionIndex]);
  }

  return updateSelectedFieldPositions(state, {
    fieldPositionKeyPaths,
    append
  });
}

export function updateSelectedBasicElements(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { pageId, elements } = payload;

  const pages = state.template.pages;
  const pageIndex = getItemIndexId(pages, pageId);
  const elementsKeyPath: KeyPath = ['template', 'pages', pageIndex, 'elements'];
  const elementsInState = getIn<BasicElement[]>(state, elementsKeyPath) ?? [];

  const allowLockedElements = elements.length === 1;
  const elementKeyPaths: KeyPath[] = elements
    .map((element) => elementsInState.findIndex((x) => x.id === element.id))
    .filter((elementIndex) => elementIndex != -1 && (allowLockedElements || !elementsInState[elementIndex].isLocked))
    .map((elementIndex) => [...elementsKeyPath, elementIndex]);

  state.selection ??= {};
  state.selection.elements = elementKeyPaths;
  return updatedSelectionBoundingRectangle(state);
}

export function updateSelectedColumns(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldId, columnIds, append } = payload;

  const columns = append ? [...state.selection.columnIds] : [];

  for (const columnId of columnIds) {
    columns.splice(columns.length, 0, columnId);
  }

  state.selection = { fieldIds: [fieldId], columnIds: columns };

  return updatedSelectionBoundingRectangle(state);
}

export function updateSelectedColumnPositions(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldPositionKeyPath, columnIds, append, rangeSelect } = payload;
  const fieldPositions = [fieldPositionKeyPath];
  const columnPositions = append || rangeSelect ? [...state.selection?.columnPositions] : [];

  // todo
  // theres we need to check here if the current selection is a column and if the column has a value set for
  // its name. if not, we need to stop processing the selection and notify the user that a name is required

  // select a range of columns
  if (rangeSelect && columnPositions.length > 0 && columnIds.length > 0) {
    // TODO these weren't really sorted since sortNumber doesn't exist here
    const sortedColumnPositions = getIn<TableColumnPosition[]>(state, [...fieldPositionKeyPath, 'columns']);
    const lastColumnSelectionIndex = sortedColumnPositions.findIndex((x) => x.columnId === columnPositions[columnPositions.length - 1]);
    const currentColumnSelectionIndex = sortedColumnPositions.findIndex((x) => x.columnId === columnIds[0]);
    let startColumnIndex = lastColumnSelectionIndex + 1;
    let endColumnIndex = currentColumnSelectionIndex - 1;

    if (lastColumnSelectionIndex > currentColumnSelectionIndex) {
      startColumnIndex = currentColumnSelectionIndex + 1;
      endColumnIndex = lastColumnSelectionIndex - 1;
    }

    for (let columnIndex = startColumnIndex; columnIndex <= endColumnIndex; columnIndex++) {
      columnPositions.push(sortedColumnPositions[columnIndex].columnId);
    }
  }

  for (const columnId of columnIds) {
    columnPositions.push(columnId);
  }

  state.selection = { fieldPositions, columnPositions };

  return updatedSelectionBoundingRectangle(state);
}

export function updateSelectedRowPositions(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldPositionKeyPath, rowNumbers, append, rangeSelect } = payload;
  const fieldPositions = [fieldPositionKeyPath];

  let rowPositions: number[];

  if (append || rangeSelect) {
    rowPositions = state.selection.rowPositions;
  }

  if (!rowPositions) {
    rowPositions = [];
  }

  for (const rowNumber of rowNumbers) {
    // select a range of rows
    if (rangeSelect && rowPositions.length > 0) {
      const lastRowSelection = rowPositions[rowPositions.length - 1];
      let startRowNumber = lastRowSelection + 1;
      let endRowNumber = rowNumber - 1;

      if (lastRowSelection > rowNumber) {
        startRowNumber = rowNumber + 1;
        endRowNumber = lastRowSelection - 1;
      }

      for (let rn = startRowNumber; rn <= endRowNumber; rn++) {
        rowPositions = insertAt(rowPositions, rowPositions.length - 1, rn);
      }
    }

    const existingRowNumberIndex = append ? rowPositions.findIndex((x) => x === rowNumber) : -1;

    if (existingRowNumberIndex > -1) {
      rowPositions = rowPositions.filter((_, idx) => idx !== existingRowNumberIndex);
    } else {
      rowPositions.push(rowNumber);
    }
  }

  const rowPositionsList = rowPositions.length > 0 ? rowPositions : null;

  state.selection = { fieldPositions: fieldPositions, rowPositions: rowPositionsList };

  return updatedSelectionBoundingRectangle(state);
}

export function updateColumnPositionWidthByOffset(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldPositionKeyPath, columnId, offsetWidth } = payload;

  const pageWidth = getIn<number>(state, [...getParentKeyPath(getParentKeyPath(fieldPositionKeyPath)), 'width']);
  const fieldPositionLeft = getIn<number>(state, [...fieldPositionKeyPath, 'x']);
  const fieldPositionWidth = getIn<number>(state, [...fieldPositionKeyPath, 'w']);
  const columnPositions = getIn<TableColumnPosition[]>(state, [...fieldPositionKeyPath, 'columns']);

  const maxWidthOffset = pageWidth - (fieldPositionLeft + fieldPositionWidth);
  const actualOffsetWidth = Math.min(maxWidthOffset, offsetWidth);

  // update overall table width
  setIn(state, [...fieldPositionKeyPath, 'w'], Math.round(fieldPositionWidth + actualOffsetWidth));

  // update width of specific column
  const columnPositionIndex = columnPositions.findIndex((p) => p.columnId === columnId);
  const columnPositionKeyPath: KeyPath = [...fieldPositionKeyPath, ...['columns', columnPositionIndex]];
  const columnPositionWidth = getIn<number>(state, [...columnPositionKeyPath, 'w']);

  setIn(state, [...columnPositionKeyPath, 'w'], Math.max(Math.round(columnPositionWidth + actualOffsetWidth), 2));

  return updateTableFieldPositionWidth(state, fieldPositionKeyPath);
}

export function updateColumnPositionWidth(state: TemplateState, keyPaths: KeyPath[], value: number): TemplateState {
  if (!keyPaths || keyPaths.length === 0) {
    return state;
  }

  const fieldPositionKeyPath = getParentKeyPath(getParentKeyPath(getParentKeyPath(keyPaths[0])));
  const pageWidth = getIn<number>(state, [...getParentKeyPath(getParentKeyPath(fieldPositionKeyPath)), 'width']);
  const fieldPositionLeft = getIn<number>(state, [...fieldPositionKeyPath, 'x']);
  const fieldPositionWidth = getIn<number>(state, [...fieldPositionKeyPath, 'w']);
  const sumOfColumnWidths = keyPaths.map((kp) => getIn<number>(state, kp)).reduce((prev, curr) => prev + curr, 0);
  const totalAvailableWidth = pageWidth - (fieldPositionLeft + fieldPositionWidth) + sumOfColumnWidths;
  const newColumnWidth = Math.max(Math.min(Math.floor(totalAvailableWidth / keyPaths.length), value), 2);

  keyPaths.forEach((keyPath) => {
    const currentWidth = getIn<number>(state, keyPath);
    const columnPositionKeyPath = getParentKeyPath(keyPath);
    const columnId = getIn<string>(state, [...columnPositionKeyPath, 'columnId']);
    const offsetWidth = newColumnWidth - currentWidth;

    state = updateColumnPositionWidthByOffset(state, {
      fieldPositionKeyPath,
      columnId,
      offsetWidth
    });
  });

  return state;
}

export function updateTableFieldPositionWidth(state: TemplateState, fieldPositionKeyPath: KeyPath): TemplateState {
  const fieldPosition = getIn<FieldPosition>(state, fieldPositionKeyPath);
  const fieldId = fieldPosition.fieldId;
  const fieldKeyPath = getFieldKeyPathById(state, fieldId);
  const columns = getIn<TemplateColumn[]>(state, [...fieldKeyPath, 'columns']).sort((a, b) => a.sortOrder - b.sortOrder);
  const columnPositions = getIn<TableColumnPosition[]>(state, [...fieldPositionKeyPath, 'columns']);

  let x = 0;
  let width = 0;

  for (const column of columns) {
    const columnPositionIndex = columnPositions.findIndex((p) => p?.columnId === column.id);

    if (columnPositionIndex === -1) {
      continue;
    }

    const columnPositionKeyPath: KeyPath = [...fieldPositionKeyPath, 'columns', columnPositionIndex];
    const columnPosition = getIn<TableColumnPosition>(state, columnPositionKeyPath);

    if (!columnPosition.hidden) {
      setIn(state, [...columnPositionKeyPath, 'x'], x);

      x += columnPosition.w + 1;
      width += columnPosition.w + 1;
    }
  }

  setIn(state, [...fieldPositionKeyPath, 'w'], width);

  return state;
}

export function updateColumnPositionHidden(state: TemplateState, keyPaths: KeyPath[], value: number): TemplateState {
  for (const keyPath of keyPaths) {
    setIn(state, keyPath, value);
    setIn(state, [...getParentKeyPath(keyPath), 'w'], 0);

    state = updateColumnPositionWidth(state, [keyPath], value ? 0 : 73);
  }

  return state;
}

export function updateRowPositionHeightByOffset(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldPositionKeyPath, rowNumber, offsetHeight } = payload;

  const fieldPositionHeight = getIn<number>(state, [...fieldPositionKeyPath, 'h']);
  const rowPositions = getIn<TableRowPosition[]>(state, [...fieldPositionKeyPath, 'rows']) ?? [];

  if (!rowPositions) {
    setIn(state, [...fieldPositionKeyPath, 'rows'], []);
  }

  // update overall table height
  setIn(state, [...fieldPositionKeyPath, 'h'], fieldPositionHeight + offsetHeight);

  // update height of specific row
  let rowPositionKeyPath: KeyPath;
  const rowPositionIndex = rowPositions.findIndex((p) => p.rowNumber === rowNumber);

  if (rowPositionIndex === -1) {
    // rowPosition doesn't exist so we need to create it
    rowPositionKeyPath = [...fieldPositionKeyPath, 'rows', rowPositions.length];
    setIn(state, [...rowPositionKeyPath, 'rowNumber'], rowNumber);

    // set height to default row height
    const defaultRowHeight = getIn<number>(state, [...fieldPositionKeyPath, 'rowHeight']);
    setIn(state, [...rowPositionKeyPath, ...['h']], defaultRowHeight);
  } else {
    rowPositionKeyPath = [...fieldPositionKeyPath, 'rows', rowPositionIndex];
  }

  const rowPositionHeight = getIn<number>(state, [...rowPositionKeyPath, 'h']);

  setIn(state, [...rowPositionKeyPath, 'h'], Math.round(rowPositionHeight + offsetHeight));

  return state;
}

export function updateRowPositionHeight(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, value } = payload;

  if (!keyPaths || keyPaths.length === 0) {
    return state;
  }

  const fieldPositionKeyPath = keyPaths[0].slice(0, keyPaths[0].length - 3);
  const pageHeight = getIn<number>(state, [...fieldPositionKeyPath.slice(0, fieldPositionKeyPath.length - 2), 'height']);
  const fieldPositionTop = getIn<number>(state, [...fieldPositionKeyPath, 'y']);
  const fieldPositionHeight = getIn<number>(state, [...fieldPositionKeyPath, 'h']);
  const rowHeights = keyPaths.map((keyPath) => getIn<number>(state, keyPath)).reduce((prev, curr) => prev + curr, 0);
  const totalAvailableHeight = pageHeight - (fieldPositionTop + fieldPositionHeight) + rowHeights;
  const newRowHeight = Math.max(Math.min(Math.floor(totalAvailableHeight / keyPaths.length), value as number), 2);

  keyPaths.forEach((keyPath) => {
    const currentHeight = getIn<number>(state, keyPath);
    const rowPositionKeyPath = keyPath.slice(0, keyPath.length - 1);
    const { rowNumber } = getIn<TableRowPosition>(state, rowPositionKeyPath);
    const offsetHeight = newRowHeight - currentHeight;

    state = updateRowPositionHeightByOffset(state, {
      fieldPositionKeyPath,
      rowNumber,
      offsetHeight
    });
  });

  return state;
}

export function updateSelectedDataLayoutPositions(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { positionKeyPath, append, selectRange } = payload;

  const dataLayoutPositions = append || selectRange ? state.selection.dataLayoutPositions ?? [] : [];

  // select a range of positions
  if (selectRange && dataLayoutPositions.length > 0 && positionKeyPath) {
    const lastSelectionKeyPath = dataLayoutPositions[dataLayoutPositions.length - 1];
    const lastSelectionSectionKeyPath = getParentKeyPath(getParentKeyPath(lastSelectionKeyPath));
    const currentSelectionSectionKeyPath = getParentKeyPath(getParentKeyPath(positionKeyPath));

    // only do a range select if the last selection was in the same section
    if (compareArrays(lastSelectionSectionKeyPath, currentSelectionSectionKeyPath)) {
      const lastSelectionIndex = lastSelectionKeyPath[lastSelectionKeyPath.length - 1] as number;
      const currentSelectionIndex = positionKeyPath[positionKeyPath.length - 1] as number;
      let startIndex = lastSelectionIndex + 1;
      let endIndex = currentSelectionIndex - 1;

      if (lastSelectionIndex > currentSelectionIndex) {
        startIndex = currentSelectionIndex + 1;
        endIndex = lastSelectionIndex - 1;
      }

      for (let index = startIndex; index <= endIndex; index++) {
        dataLayoutPositions.push([...getParentKeyPath(positionKeyPath), index]);
      }
    }
  }

  dataLayoutPositions.push(positionKeyPath);

  //when selecting a field on the template we want to update the selection of the fieldIds to
  const fieldIds = (dataLayoutPositions || [])
    .map((dataLayoutPositionsKeyPath) => getIn<DataLayoutPosition>(state, dataLayoutPositionsKeyPath)?.fieldId)
    .filter((fieldId) => !!fieldId);

  state.selection = { dataLayoutPositions, fieldIds: fieldIds };

  return state;
}

export function updateSelectedDataLayoutSections(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { sectionKeyPath, append } = payload;

  const dataLayoutSections = append ? [...(state.selection.dataLayoutSections as KeyPath[])] : [];

  dataLayoutSections.push(sectionKeyPath);
  state.selection = { dataLayoutSections };

  return state;
}

export function updateSelectedDataLayoutTabs(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { tabKeyPath, append } = payload;

  const dataLayoutTabs = append ? [...state.selection.dataLayoutTabs] : [];

  dataLayoutTabs.push(tabKeyPath);
  state.selection = { dataLayoutTabs };
  state.currentTabKeyPath = tabKeyPath;

  return state;
}

export function deleteDataLayoutSection(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { tabKeyPath } = payload;
  const sectionsKeyPath = [...tabKeyPath, 'sections'];

  deleteSelectedItems(state);

  if (getIn<DataLayoutSection[]>(state, sectionsKeyPath).length === 0) {
    setIn(state, [...sectionsKeyPath, 0], {
      id: getGuid(),
      name: '',
      sortNumber: 0,
      positions: []
    });
  }

  return state;
}

export function moveDataLayoutPosition(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { newSectionKeyPath, selectedKeyPaths, insertNextToId, insertDirection } = payload;
  const newSectionKeyFullPath: KeyPath = [...newSectionKeyPath, 'positions'];

  if (!getIn<boolean>(state, newSectionKeyPath)) {
    setIn(state, newSectionKeyPath, {
      id: getGuid(),
      sortNumber: 0,
      positions: []
    });
  }

  const positionIds = selectedKeyPaths.map((keyPath) => getIn(state, [...keyPath, 'id']));

  state = moveItemsByKeyPath(state, newSectionKeyFullPath, 'sortNumber', selectedKeyPaths, insertNextToId, insertDirection);

  // re-select the positions
  const newSectionPositions = getIn<DataLayoutSection[]>(state, newSectionKeyFullPath);
  const newSelectionKeyPaths: KeyPath[] = positionIds.map((positionId) => [
    ...newSectionKeyFullPath,
    newSectionPositions.findIndex((p) => p.id === positionId)
  ]);

  state.selection = { dataLayoutPositions: newSelectionKeyPaths };
  state.currentTabKeyPath = getParentKeyPath(getParentKeyPath(newSectionKeyPath));

  return state;
}

export function setColumnInitialValues(state: TemplateState, fieldType: number, keyPath: KeyPath, rawDateFormat: TemplateDateFormat): TemplateState {
  deleteIn(state, [...keyPath, 'formatting']);
  deleteIn(state, [...keyPath, 'items']);
  deleteIn(state, [...keyPath, 'multiSelect']);
  deleteIn(state, [...keyPath, 'displayColumn']);
  deleteIn(state, [...keyPath, 'dropDownItems']);
  deleteIn(state, [...keyPath, 'imageQuality']);
  deleteIn(state, [...keyPath, 'retainImageAsAttachment']);
  deleteIn(state, [...keyPath, 'dateStamp']);
  deleteIn(state, [...keyPath, 'clearOnFieldChange']);
  deleteIn(state, [...keyPath, 'maskOnComplete']);
  deleteIn(state, [...keyPath, 'lockFirstValue']);
  deleteIn(state, [...keyPath, 'useCurrent']);

  if (fieldType === FieldTypes.Database) {
    setIn(state, [...keyPath, 'allowOther'], true);
  } else if (fieldType === FieldTypes.DropDown) {
    setIn(state, [...keyPath, 'allowOther'], false);
  } else {
    deleteIn(state, [...keyPath, 'allowOther']);
  }

  if (fieldType === FieldTypes.Date) {
    setIn(state, [...keyPath, 'dateTimeFormat'], fixDotNetDateFormat(rawDateFormat.shortDatePattern));
  } else if (fieldType === FieldTypes.DateTime) {
    setIn(state, [...keyPath, 'dateTimeFormat'], fixDotNetDateFormat(rawDateFormat.fullDateTimePattern));
  } else if (fieldType === FieldTypes.Time) {
    setIn(state, [...keyPath, 'dateTimeFormat'], fixDotNetDateFormat(rawDateFormat.shortTimePattern));
  } else {
    deleteIn(state, [...keyPath, 'dateTimeFormat']);
  }

  return state;
}

export function setColumnPositionInitialValues(state: TemplateState, fieldType: number, keyPath: KeyPath): TemplateState {
  const columnDefaultSettings = getFieldPositionDefaultSettings(state.defaultSettings, fieldType, state.template.defaultFieldDisplaySettings);

  setIn(state, [...keyPath, 'displayType'], columnDefaultSettings.displayType);

  if (
    fieldType === FieldTypes.TextBox ||
    fieldType === FieldTypes.Barcode ||
    fieldType === FieldTypes.DropDown ||
    fieldType === FieldTypes.TextArea ||
    fieldType === FieldTypes.DateTime ||
    fieldType === FieldTypes.Date ||
    fieldType === FieldTypes.Time ||
    fieldType === FieldTypes.Database ||
    fieldType === FieldTypes.Number ||
    fieldType === FieldTypes.AutoNumber
  ) {
    const currentAlign = getIn<string>(state, [...keyPath, 'align']);

    if (currentAlign === 'Top Left') {
      setIn(state, [...keyPath, 'align'], 'Left');
    }
  } else if (fieldType === FieldTypes.Image || fieldType === FieldTypes.Sketch) {
    setIn(state, [...keyPath, 'align'], 'Top Left');
  }

  return state;
}

export function addFieldPosition(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPath: newFieldPositionKeyPath } = addFieldPositionReturnResult(state, payload);

  return updateSelectedFieldPositions(state, {
    fieldPositionKeyPaths: [newFieldPositionKeyPath]
  });
}

export function addFieldPositionReturnResult(state: TemplateState, payload: TemplateEditorPayload): { keyPath: KeyPath; fieldId: string } {
  const { pageKeyPath, type, x, y, width, height, checkBoxGroupItemId, rawDateFormat, features, tableFieldProperties } = payload;
  const positionsKeyPath = [...pageKeyPath, 'fieldPositions'];

  let field = payload.field;
  const items = getIn<FieldPosition[]>(state, positionsKeyPath) ?? [];

  if (!field) {
    const fieldId = getGuid();

    state = createField(state, fieldId, type, rawDateFormat, features, !!payload.isFieldCommitted, tableFieldProperties);
    field = getField(state, fieldId);

    if (type === FieldTypes.FileAttachment) {
      analyticsTrack('File Attachment Field - Added to template', {
        view: 'Form View',
        templateId: state.template.id,
        templateName: state.template.name,
        fieldId: fieldId,
        fieldName: field.name
      });
    } else if (type === FieldTypes.eSignature || type === FieldTypes.eSignatureInitials) {
      analyticsTrack('eSign Field Added to a Template', {
        type: type === FieldTypes.eSignature ? 'Signature' : 'Initials',
        view: 'Form View',
        templateId: state.template.id,
        templateName: state.template.name,
        fieldId,
        fieldName: field.name
      });
    }
  }

  const defaultSettings = getFieldPositionDefaultSettings(state.defaultSettings, type, state.template.defaultFieldDisplaySettings);

  const fieldPosition: FieldPosition = {
    id: getGuid(),
    fieldId: field.id,
    x,
    y,
    w: width || defaultSettings.width,
    h: height || defaultSettings.height,
    color: defaultSettings.color,
    backgroundColor: defaultSettings.backgroundColor,
    focusColor: defaultSettings.focusColor,
    fontName: defaultSettings.fontName,
    fontSize: defaultSettings.fontSize,
    displayType: defaultSettings.displayType,
    align: defaultSettings.align,
    useDefaultDisplaySettings: true
  };

  if (type === FieldTypes.TextBox || type === FieldTypes.Barcode || type === FieldTypes.TextArea || type === FieldTypes.Number || type === FieldTypes.Table) {
    const tabIndex = getHighestTabIndex(state) + 1;

    fieldPosition.tabIndex = tabIndex;
  }

  if (type === FieldTypes.Table) {
    const rowCount = tableFieldProperties?.rowCount || field.rowCount;
    const rowHeight = Math.round(height / rowCount);
    const rowPositions = Array(rowCount)
      .fill(0)
      .map((_, rowNumber) => ({ rowNumber, h: rowHeight }));

    fieldPosition.rows = rowPositions;
    fieldPosition.rowHeight = rowHeight;
    fieldPosition.numberOfRows = rowCount;
    fieldPosition.startingRow = 1;

    let columnX = 0;

    const defaultColumnWidth = field.columns ? Math.round(width / field.columns.length) : 0;
    //create column positions from field.columns if exists.
    let columnPositions = field.columns?.map((column, index) => {
      const columnWidth = tableFieldProperties?.columns[index]?.width || defaultColumnWidth;
      const transparent = tableFieldProperties?.columns[index]?.transparent || false;
      const columnDefaultSettings = getFieldPositionDefaultSettings(state.defaultSettings, column.type, state.template.defaultFieldDisplaySettings);
      const columnPosition: TableColumnPosition = {
        columnId: column.id,
        w: columnWidth,
        x: columnX,
        hidden: false,
        transparent: transparent,
        displayType: columnDefaultSettings.displayType
      };

      columnX += defaultColumnWidth + 1;

      return columnPosition;
    });

    //else create from table field properties
    if (columnPositions == null) {
      columnPositions = tableFieldProperties?.columns?.map((column) => {
        const columnDefaultSettings = getFieldPositionDefaultSettings(state.defaultSettings, column.type, state.template.defaultFieldDisplaySettings);
        const columnPosition: TableColumnPosition = {
          columnId: getGuid(),
          w: column.width || defaultColumnWidth,
          x: columnX,
          hidden: false,
          transparent: column.transparent || false,
          displayType: column.type || columnDefaultSettings.displayType
        };

        columnX += defaultColumnWidth + 1;

        return columnPosition;
      });
    }
    fieldPosition.columns = columnPositions;
  } else if (type === FieldTypes.CheckBoxGroup) {
    if (checkBoxGroupItemId) {
      fieldPosition.itemId = checkBoxGroupItemId;
    } else {
      // use the first item
      const firstItemId = field.items[0].id;
      fieldPosition.itemId = firstItemId;
    }
  }

  items.push(fieldPosition);
  setIn(state, positionsKeyPath, items);

  const newFieldPositionKeyPath = [...positionsKeyPath, ...[items.length - 1]];

  return { keyPath: newFieldPositionKeyPath, fieldId: fieldPosition.fieldId };
}

export function addBasicElement(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { pageKeyPath, element } = payload;
  const newElement = {
    ...element,
    id: getGuid() //always ensure any added element has a new id
  };

  const page = getIn<TemplatePage>(state, pageKeyPath);
  const elementsKeyPath = [...pageKeyPath, 'elements'];
  const items = getIn<BasicElement[]>(state, elementsKeyPath) ?? [];

  items.push(newElement);
  setIn(state, elementsKeyPath, items);

  return updateSelectedBasicElements(state, {
    pageId: page.id,
    elements: [newElement]
  });
}

export function removeBasicElements(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths } = payload;

  const pageKeyPaths = keyPaths
    .map((elementsKeyPath) => {
      const pageKeyPath = getParentKeyPath(elementsKeyPath);
      return pageKeyPath;
    })
    .filter((pageKeyPath, index, all) => all.indexOf(pageKeyPath) === index);

  const elementIdsToDelete = keyPaths.map((elementsKeyPath) => getIn<BasicElement>(state, elementsKeyPath)?.id);

  for (const pageKeyPath of pageKeyPaths) {
    const currentElements = getIn<BasicElement[]>(state, pageKeyPath) ?? [];
    const modifiedElements = currentElements.filter((element) => !elementIdsToDelete.some((id) => id === element.id));
    setIn(state, pageKeyPath, modifiedElements);
  }

  const reducedSelectedElements = (state.selection?.elements || []).filter(
    (selectedKeyPath) => !keyPaths.some((keyPath) => keyPath.every((value, index) => selectedKeyPath[index] === value))
  );

  state.selection = {
    ...state.selection,
    elements: reducedSelectedElements
  };

  return state;
}

export function addDataLayoutPosition(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { sectionKeyPath, type, insertNextToId, insertDirection, rawDateFormat, features } = payload;

  let field: TemplateField = payload.field ? { ...payload.field } : null;

  const positionsKeyPath = [...sectionKeyPath, 'positions'];
  const items = getIn<DataLayoutPosition[]>(state, positionsKeyPath) ?? [];
  const insertNextToItem = items.find((item) => item.id === insertNextToId);
  const sortNumber = insertNextToItem ? insertNextToItem.sortNumber + (insertDirection === 'up' ? -0.1 : 0.1) : 999999999;

  if (!field) {
    const fieldId = getGuid();
    state = createField(state, fieldId, type, rawDateFormat, features);
    field = getField(state, fieldId);

    if (type === FieldTypes.FileAttachment) {
      analyticsTrack('File Attachment Field - Added to template', {
        view: 'List View',
        templateId: state.template.id,
        templateName: state.template.name,
        fieldId,
        fieldName: field.name
      });
    } else if (type === FieldTypes.eSignature || type === FieldTypes.eSignatureInitials) {
      analyticsTrack('eSign Field Added to a Template', {
        Type: type === FieldTypes.eSignature ? 'Signature' : 'Initials',
        view: 'List View',
        templateId: state.template.id,
        templateName: state.template.name,
        fieldId,
        fieldName: field.name
      });
    }
  }

  const itemId = getGuid();
  const dataLayoutPosition: DataLayoutPosition = {
    id: itemId,
    sortNumber,
    fieldId: field.id
  };

  if (type === FieldTypes.CheckBoxGroup) {
    const checkBoxGroupItems =
      field.items?.map((fieldItem) => ({
        id: getGuid(),
        itemId: fieldItem.id
      })) ?? [];

    dataLayoutPosition.checkBoxGroupItems = checkBoxGroupItems;
  }

  items.push(dataLayoutPosition);

  const reorderedItems = reorderItems(items, 'sortNumber');
  setIn(state, positionsKeyPath, reorderedItems);

  const positionIndex = reorderedItems.findIndex((x) => x.id === itemId);

  return updateSelectedDataLayoutPositions(state, {
    positionKeyPath: [...positionsKeyPath, positionIndex]
  });
}

export function addDataLayoutSection(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { tabKeyPath, insertNextToId, insertDirection } = payload;

  const sectionsKeyPath = [...tabKeyPath, 'sections'];
  const items = getIn<DataLayoutSection[]>(state, sectionsKeyPath);
  const insertNextToItem = items.find((item) => item.id === insertNextToId);
  const sortNumber = !!insertNextToItem ? insertNextToItem.sortNumber + (insertDirection === 'up' ? -0.1 : 0.1) : insertDirection === 'up' ? -0.1 : 9999999;
  const sectionId = getGuid();

  items.push({
    id: sectionId,
    name: '',
    sortNumber,
    positions: []
  });

  const reorderedItems = reorderItems(items, 'sortNumber');

  setIn(state, sectionsKeyPath, reorderedItems);

  const sectionIndex = reorderedItems.findIndex((x) => x.id === sectionId);

  return updateSelectedDataLayoutSections(state, {
    sectionKeyPath: [...sectionsKeyPath, ...[sectionIndex]]
  });
}

export function addDataLayoutTab(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { layoutKeyPath } = payload;
  const tabsKeyPath = [...layoutKeyPath, 'dataLayoutTabs'];

  const items = getIn<DataLayoutTab[]>(state, tabsKeyPath);
  const sortNumber = 999999;
  const tabId = getGuid();

  items.push({
    id: tabId,
    name: 'New Tab',
    sortNumber,
    sections: [
      {
        id: getGuid(),
        name: '',
        sortNumber: 1,
        positions: []
      }
    ]
  });

  const reorderedItems = reorderItems(items, 'sortNumber');

  setIn(state, tabsKeyPath, reorderedItems);

  const tabIndex = reorderedItems.findIndex((x) => x.id === tabId);

  return updateSelectedDataLayoutTabs(state, {
    tabKeyPath: [...tabsKeyPath, ...[tabIndex]]
  });
}

export function addDefaultDataLayoutTab(state: TemplateState, keypath: KeyPath): TemplateState {
  const items = getIn<DataLayoutTab[]>(state, keypath);

  const sortNumber = 999999;
  const tabId = getGuid();
  const tabName = 'Tab 1';

  items.push({
    id: tabId,
    name: tabName,
    sortNumber,
    sections: [
      {
        id: getGuid(),
        name: '',
        sortNumber: 1,
        positions: []
      }
    ]
  });

  const reorderedItems = reorderItems(items, 'sortNumber');

  setIn(state, keypath, reorderedItems);

  const tabIndex = reorderedItems.findIndex((x) => x.id === tabId);

  return updateSelectedDataLayoutTabs(state, {
    tabKeyPath: [...keypath, ...[tabIndex]]
  });
}

export function reorderColumn(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldId, columnId, direction } = payload;

  const fieldKeyPath = getFieldKeyPathById(state, fieldId);
  const columnsKeyPath: KeyPath = [...fieldKeyPath, 'columns'];
  const columns = getIn<TemplateColumn[]>(state, columnsKeyPath);
  const sortedColumns = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);
  const currentColumnSortedIndex = sortedColumns.findIndex((x) => x.id === columnId);
  let columnToSwitch: TemplateColumn;

  if (direction === 'up' && currentColumnSortedIndex > 0) {
    columnToSwitch = sortedColumns[currentColumnSortedIndex - 1];
  } else if (direction === 'down' && currentColumnSortedIndex < sortedColumns.length - 1) {
    columnToSwitch = sortedColumns[currentColumnSortedIndex + 1];
  }

  if (!!columnToSwitch) {
    const columnToSwitchSortOrder = columnToSwitch.sortOrder;
    const columnToSwitchIndex = columns.findIndex((x) => x.id === columnToSwitch.id);
    const currentColumnIndex = columns.findIndex((x) => x.id === columnId);
    const currentColumnSortOrder = columns[currentColumnIndex].sortOrder;

    columns[columnToSwitchIndex].sortOrder = currentColumnSortOrder;
    columns[currentColumnIndex].sortOrder = columnToSwitchSortOrder;
  }

  setIn(state, columnsKeyPath, columns);

  const columnPositionKeyPaths = getColumnPositionKeyPathsByColumnId(state, fieldId, columnId);

  for (const columnPositionKeyPath of columnPositionKeyPaths) {
    // The X coordinates need to be updated now that they columns have been reordered
    state = updateTableFieldPositionWidth(state, getParentKeyPath(getParentKeyPath(columnPositionKeyPath)));
  }

  return state;
}

export function addColumn(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldId } = payload;

  const fieldKeyPath = getFieldKeyPathById(state, fieldId);
  const columnsKeyPath = [...fieldKeyPath, 'columns'];
  const columns = getIn<TemplateColumn[]>(state, columnsKeyPath);

  if (columns.length === 25) {
    return state;
  }

  const sortOrder = (Math.max(...columns.map((x) => x.sortOrder)) || 0) + 1;
  // get first available column number
  let columnNumber = 0;

  for (const column of [...columns.sort((a, b) => a.columnNumber - b.columnNumber)]) {
    if (column.columnNumber === columnNumber) {
      columnNumber++;
    }
  }

  const columnId = getGuid();

  columns.push({
    id: columnId,
    name: `Column ${columns.length + 1}`,
    type: FieldTypes.TextBox,
    columnNumber,
    sortOrder
  });

  setIn(state, columnsKeyPath, columns);

  // now add columnPositions for each fieldPosition
  const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, fieldId);

  for (const fieldPositionKeyPath of fieldPositionKeyPaths) {
    const columnPositionsKeyPath: KeyPath = [...fieldPositionKeyPath, 'columns'];
    const columnPositions = getIn<TableColumnPosition[]>(state, columnPositionsKeyPath);

    columnPositions.push({
      columnId,
      w: 0,
      hidden: false,
      displayType: 0
    });

    setIn(state, columnPositionsKeyPath, columnPositions);

    const columnPositionWidthKeyPath = [...columnPositionsKeyPath, columnPositions.length - 1, 'w'];
    state = updateColumnPositionWidth(state, [columnPositionWidthKeyPath], 40);
  }

  return updatedSelectionBoundingRectangle(state);
}

export function deleteColumn(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldId, columnId } = payload;

  const columnPositionKeyPaths = getColumnPositionKeyPathsByColumnId(state, fieldId, columnId);
  const fieldKeyPath = getFieldKeyPathById(state, fieldId);
  const columnsKeyPath: KeyPath = [...fieldKeyPath, 'columns'];
  let columns = getIn<TemplateColumn[]>(state, columnsKeyPath).filter((column) => column.id !== columnId);

  columns = reorderItems(columns, 'sortOrder');
  setIn(state, columnsKeyPath, columns);

  for (const columnPositionKeyPath of columnPositionKeyPaths) {
    deleteIn(state, columnPositionKeyPath);
    // update fieldposition width now that the column is removed
    state = updateTableFieldPositionWidth(state, getParentKeyPath(getParentKeyPath(columnPositionKeyPath)));
  }

  return updatedSelectionBoundingRectangle(state);
}

export function updateColumnFieldType(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, rawDateFormat, value: fieldType } = payload;

  for (const keyPath of keyPaths) {
    const fieldId = getIn<string>(state, [...getParentKeyPath(getParentKeyPath(getParentKeyPath(keyPath))), 'id']);
    const columnKeyPath = getParentKeyPath(keyPath);
    const columnId = getIn<string>(state, [...columnKeyPath, 'id']);
    const previousFieldType = getIn<number>(state, keyPath);

    setIn(state, keyPath, fieldType);

    if (previousFieldType !== fieldType) {
      state = setColumnInitialValues(state, fieldType as number, columnKeyPath, rawDateFormat);

      const columnPositionKeyPaths = getColumnPositionKeyPathsByColumnId(state, fieldId, columnId);

      for (const columnPositionKeyPath of columnPositionKeyPaths) {
        state = setColumnPositionInitialValues(state, fieldType as number, columnPositionKeyPath);
      }
    }
  }

  return state;
}

export function getFieldsNotOnListView(state: TemplateState): Record<string, { fieldId: string; pageNumber: number }> {
  const pages = state.template.pages;
  const fields = {};

  for (const page of [...pages.sort((a, b) => a.number - b.number)]) {
    const fieldPositions = (page.fieldPositions ?? []) as FieldPosition[];

    for (const fieldPosition of [
      ...fieldPositions.sort((a, b) => {
        const compare1 = a.y - b.y;
        const compare2 = a.x - b.x;

        if (compare1 !== 0) {
          return compare1;
        }

        return compare2;
      })
    ]) {
      const layouts = getDataLayoutPositionKeyPathsByFieldId(state, fieldPosition.fieldId);

      if (layouts.length === 0 && !fields[fieldPosition.fieldId]) {
        fields[fieldPosition.fieldId] = {
          fieldId: fieldPosition.fieldId,
          pageNumber: page.number
        };
      }
    }
  }

  return fields;
}

export function autoBuildListView(state: TemplateState): TemplateState {
  const tabsKeyPath: KeyPath = ['template', 'dataLayouts', 0, 'dataLayoutTabs'];
  const fieldsNotOnListView = getFieldsNotOnListView(state);
  const pageToTabMap: Record<string, { tabName: string; tabIndex: number }> = {};

  for (const fieldId of Object.keys(fieldsNotOnListView)) {
    const field = fieldsNotOnListView[fieldId];

    if (!pageToTabMap[field.pageNumber]) {
      const tabName = `Page ${field.pageNumber}`;
      let tabs = getIn<DataLayoutTab[]>(state, tabsKeyPath);
      let tabIndex = tabs.findIndex((tab) => tab.name === tabName);

      if (tabIndex === -1) {
        // create new tab
        tabIndex = tabs.length;
        tabs = insertAt(tabs, tabIndex, {
          id: getGuid(),
          name: tabName,
          sections: []
        });

        tabs = reorderItems(tabs, 'sortNumber');
        setIn(state, tabsKeyPath, tabs);
      }

      const sectionsKeyPath: KeyPath = [...tabsKeyPath, tabIndex, 'sections'];

      if (getIn<DataLayoutSection[]>(state, sectionsKeyPath).length === 0) {
        setIn(state, [...sectionsKeyPath, 0], {
          id: getGuid(),
          name: '',
          sortNumber: 0,
          positions: []
        });
      }

      pageToTabMap[field.pageNumber] = {
        tabName,
        tabIndex
      };
    }

    const dataLayoutPosition: DataLayoutPosition = {
      id: getGuid(),
      fieldId: field.fieldId
    };

    const f = getField(state, field.fieldId);

    if (f.type === FieldTypes.CheckBoxGroup) {
      const checkBoxGroupItems = [];

      for (const item of f.items) {
        checkBoxGroupItems.push({
          id: getGuid(),
          itemId: item.id
        });
      }

      dataLayoutPosition.checkBoxGroupItems = checkBoxGroupItems;
    }

    const positionsKeyPath = [...tabsKeyPath, pageToTabMap[field.pageNumber].tabIndex, 'sections', 0, 'positions'];
    let positions = getIn<DataLayoutPosition[]>(state, positionsKeyPath) || [];

    positions.push(dataLayoutPosition);
    positions = reorderItems(positions, 'sortNumber');
    setIn(state, positionsKeyPath, positions);
  }

  return state;
}

export function formulaEditorFocus(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  state.formulaEditor.active = true;
  state.formulaEditor.property = payload.property;
  state.formulaEditor.row = payload.row;
  state.formulaEditor.rowCount = payload.rowCount;
  state.formulaEditor.mode = payload.mode;
  state.formulaEditor.maxLength = payload.maxLength;

  return state;
}

export function formulaEditorBlur(state: TemplateState): TemplateState {
  state.formulaEditor.active = false;
  state.formulaEditor.property = null;
  state.formulaEditor.row = null;
  state.formulaEditor.rowCount = null;
  state.formulaEditor.mode = null;

  return state;
}

export function editorFormulaChange(state: TemplateState, value: unknown) {
  const { formulaEditor } = state;

  if (!formulaEditor.row) {
    formulaEditor.value = value;

    return state;
  }

  const valueIndex = formulaEditor.row - 1;
  const tableValue = formulaEditor.tableValue;

  if (tableValue.length <= valueIndex) {
    for (let i = tableValue.length; i < valueIndex; i++) {
      tableValue.push('');
    }
  }

  tableValue[valueIndex] = value;
  formulaEditor.tableValue = tableValue;

  return state;
}

/**
 * Sets the isDirty flag on the template state.
 *
 * NOTE: this function modifies the immerjs draft state object.
 *
 * @param state The immerjs draft state (can be modified).
 * @param isDirty True if the template state has changed, false otherwise.
 */
export function setIsDirty(state: TemplateState, isDirty = true) {
  state.isDirty = isDirty;

  return state;
}

function copyField(existingField: TemplateField, fields: TemplateField[], replicateField = false): TemplateField {
  let newField: TemplateField = null;
  if (replicateField) {
    return { ...existingField, shared: false };
  } else {
    newField = { ...existingField, id: getGuid(), shared: false };
  }

  // Generate a unique name
  let highestCopyNumber = 0;
  let newFieldName = existingField.name;
  const nameMatch = newFieldName.match(/ ([0-9]+)$/);

  if (nameMatch) {
    newFieldName = newFieldName.substring(0, nameMatch.index);
    highestCopyNumber = parseFloat(nameMatch[0]);
  }

  const matchingFields = fields.filter((x) => x.name.includes(newFieldName));

  if (matchingFields.length > 0) {
    for (const matchingField of matchingFields) {
      const parsedFieldName = matchingField.name.replace(newFieldName, '');
      if (parsedFieldName.match(/^ -{0,1}\d+$/)) {
        highestCopyNumber = Math.max(highestCopyNumber, parseFloat(parsedFieldName));
      }
    }
    newFieldName += ` ${highestCopyNumber + 1}`;
  }

  newField.name = newFieldName;

  if (existingField.columns) {
    newField.columns = [];

    for (let columnIndex = 0; columnIndex < existingField.columns.length; columnIndex++) {
      const existingColumn = existingField.columns[columnIndex];
      const newColumn = { ...existingColumn, id: getGuid() };

      if (existingColumn.items) {
        newColumn.items = [];

        for (let columnItemIndex = 0; columnItemIndex < existingColumn.items.length; columnItemIndex++) {
          const existingItem = existingColumn.items[columnItemIndex];
          const newItem = { ...existingItem, id: getGuid() };

          newColumn.items.push(newItem);
        }
      }

      newField.columns.push(newColumn);
    }
  }

  if (existingField.items) {
    newField.items = [];

    for (let i = 0; i < existingField.items.length; i++) {
      const existingItem = existingField.items[i];
      const id = getGuid();

      newField.items.push({ ...existingItem, id: id, fieldId: id });
    }
  }

  if (existingField.validationSettings) {
    newField.validationSettings = [];

    for (let i = 0; i < existingField.validationSettings.length; i++) {
      const existingValidationSetting = existingField.validationSettings[i];
      const { criteriaType, inputValues } = existingValidationSetting;
      const id = getGuid();
      const formula = mapCriteriaToFormula(criteriaType, newField.name, inputValues);

      newField.validationSettings.push({ ...existingValidationSetting, id, formulas: [formula] });
    }
  }

  return newField;
}

function copyBasicElementPosition(
  existingBasicElement: BasicElement,
  destinationPageKeyPath: KeyPath,
  state: TemplateState,
  pasteOffset: number
): TemplateState {
  const pageWidth = getIn<number>(state, [...destinationPageKeyPath, ...['width']]);
  const pageHeight = getIn<number>(state, [...destinationPageKeyPath, ...['height']]);
  const elements = getIn<BasicElement[]>(state, [...destinationPageKeyPath, 'elements']) ?? [];

  const noPasteOffset = pasteOffset <= 0;
  const remainingWidth = pageWidth - existingBasicElement.x;
  const remainingHeight = pageHeight - existingBasicElement.y;
  const maxOffsetMultiplier = noPasteOffset ? 0 : Math.min(Math.floor(remainingWidth / pasteOffset), Math.floor(remainingHeight / pasteOffset));

  const findFirstAvailableOffset = () =>
    Array.from(Array(maxOffsetMultiplier))
      .map((_this, index) => pasteOffset * index)
      .find((offset) => {
        const x = existingBasicElement.x + offset;
        const y = existingBasicElement.y + offset;
        const matchingElementPosition = elements.find((element) => element.x === x && element.y === y);
        return !matchingElementPosition;
      });

  const calculatedOffset = noPasteOffset ? 0 : findFirstAvailableOffset();
  const newOffset = calculatedOffset == null ? maxOffsetMultiplier * pasteOffset : calculatedOffset;

  return addBasicElement(state, {
    pageKeyPath: destinationPageKeyPath,
    element: {
      ...existingBasicElement,
      x: existingBasicElement.x + newOffset,
      y: existingBasicElement.y + newOffset
    }
  });
}

function copyFieldPosition(
  existingFieldPosition: FieldPosition,
  originalField: TemplateField,
  copiedField: TemplateField,
  pageWidth: number,
  pageHeight: number,
  fieldPositions: FieldPosition[]
): FieldPosition {
  const copiedFieldPosition: FieldPosition = { ...existingFieldPosition, id: getGuid(), fieldId: copiedField.id };
  const { w, h } = existingFieldPosition;
  const { itemId } = existingFieldPosition;
  let { x, y } = existingFieldPosition;

  for (const fieldPosition of fieldPositions.slice().sort((a, b) => a.x + a.y - (b.x + b.y))) {
    if (fieldPosition.x === x && fieldPosition.y === y && fieldPosition.w === w && fieldPosition.h === h) {
      x += DEFAULT_PASTE_OFFSET;
      y += DEFAULT_PASTE_OFFSET;
    }
  }

  copiedFieldPosition.x = Math.min(x + w, pageWidth) - w;
  copiedFieldPosition.y = Math.min(y + h, pageHeight) - h;

  if (copiedField.type === FieldTypes.CheckBoxGroup) {
    const originalFieldItems = originalField.items;

    if (originalFieldItems) {
      const itemIndex = originalFieldItems.findIndex((item) => item.id === itemId);

      if (itemIndex > -1) {
        copiedFieldPosition.itemId = copiedField.items[itemIndex].id;
      }
    }
  }

  const columns = copiedFieldPosition.columns;

  if (columns) {
    for (let i = 0; i < columns.length; i++) {
      const columnIndex = originalField.columns.findIndex((c) => c.id === copiedFieldPosition.columns[i].columnId);
      copiedFieldPosition.columns[i].columnId = copiedField.columns[columnIndex].id;
    }
  }

  return copiedFieldPosition;
}

function copyDataLayoutPosition(existingDataLayoutPosition: DataLayoutPosition, copiedField: TemplateField, originalField: TemplateField) {
  const copiedDataLayoutPosition: DataLayoutPosition = {
    ...existingDataLayoutPosition,
    id: getGuid(),
    fieldId: copiedField.id,
    sortNumber: existingDataLayoutPosition.sortNumber + 0.1
  };

  // update the checkBoxGroupItems to reference the new copied items
  if (existingDataLayoutPosition.checkBoxGroupItems) {
    const checkBoxGroupItems: CheckboxGroupItem[] = [];
    const originalItems = originalField.items;
    const copiedItems = copiedField.items;

    for (const existingCheckBoxGroupItem of existingDataLayoutPosition.checkBoxGroupItems) {
      const itemIndex = originalItems.findIndex((x) => x.id === existingCheckBoxGroupItem.itemId);
      if (itemIndex > -1) {
        const copiedItem = copiedItems[itemIndex];
        if (copiedItem) {
          checkBoxGroupItems.push({
            id: getGuid(),
            itemId: copiedItem.id
          });
        }
      }
    }

    copiedDataLayoutPosition.checkBoxGroupItems = checkBoxGroupItems;
  }

  return copiedDataLayoutPosition;
}

function createField(
  state: TemplateState,
  id: string,
  fieldType: number,
  rawDateFormat: TemplateDateFormat,
  features: TemplateFeature,
  isFieldCommitted = false,
  tableFieldProperties: TemplateTableFieldProperties = null
): TemplateState {
  const defaultSettings = getFieldDefaultSettings(state.defaultSettings, fieldType, features);
  const field: TemplateField = {
    id,
    name: getDefaultName(state, fieldType),
    type: fieldType,
    color: defaultSettings.color,
    fontName: defaultSettings.fontName,
    fontSize: defaultSettings.fontSize,
    allowOther: fieldType === FieldTypes.Database,
    isFieldCommitted
  };

  // todo set default properties
  if (fieldType === FieldTypes.Table) {
    field.columns =
      tableFieldProperties?.columns == null
        ? Array(3)
            .fill(0)
            .map((_, columnNumber) => ({
              id: getGuid(),
              name: `Column ${columnNumber + 1}`,
              columnNumber,
              sortOrder: columnNumber,
              type: 0
            }))
        : tableFieldProperties.columns.map((column, columnNumber) => ({
            id: getGuid(),
            name: column.name ?? `Column ${columnNumber + 1}`,
            columnNumber,
            sortOrder: columnNumber,
            type: column.type || 0
          }));
    field.rowCount = 4;
  } else if (fieldType === FieldTypes.AutoNumber) {
    field.seed = 1000;
  } else if (fieldType === FieldTypes.CheckBoxGroup) {
    field.items = [
      {
        id: getGuid(),
        displayName: 'Checkbox 1',
        value: 'Checkbox 1',
        fieldId: id,
        sortNumber: 1
      }
    ];
  } else if (fieldType === FieldTypes.Signature) {
    field.signingMethod = 2;
  } else if (fieldType === FieldTypes.Date) {
    field.dateTimeFormat = fixDotNetDateFormat(rawDateFormat.shortDatePattern);
  } else if (fieldType === FieldTypes.Time) {
    field.dateTimeFormat = fixDotNetDateFormat(rawDateFormat.shortTimePattern);
  } else if (fieldType === FieldTypes.DateTime) {
    field.dateTimeFormat = fixDotNetDateFormat(rawDateFormat.fullDateTimePattern);
  } else if (fieldType === FieldTypes.FileAttachment) {
    field.maxFileSize = defaultSettings.maxFileSize;
    field.maxFileCount = defaultSettings.maxFileCount;
    field.allowedFileTypes = defaultSettings.allowedFileTypes as string[];
  } else if (fieldType === FieldTypes.Audio) {
    field.maxLength = defaultSettings.audioMaxLength;

    //Trigger: User adds an audio capture field to their template
    analyticsTrack('Audio Capture Field - Added To Template', {});
  }

  state.template.fields.push(field as TemplateField);

  return state;
}

function getBoundingRectangle(fieldPositions?: FieldPosition[], elements?: BasicElement[]): BoundingRectangle {
  const positions = (
    fieldPositions?.map((position) => {
      return { x: position.x, y: position.y, w: position.w, h: position.h };
    }) || []
  ).concat(
    elements?.map((element) => {
      return { x: element.x, y: element.y, w: element.width, h: element.height };
    }) || []
  );

  const boundingRectangle: BoundingRectangle = {
    left: Math.min(...positions.map((position) => position.x)),
    top: Math.min(...positions.map((position) => position.y)),
    right: Math.max(...positions.map((position) => position.x + position.w)),
    bottom: Math.max(...positions.map((position) => position.y + position.h))
  };
  return boundingRectangle;
}

function updateNumberOfRows(state: TemplateState, fieldPositionKeyPath: KeyPath, numberOfRows: number) {
  const fieldPosition = getIn<FieldPosition>(state, fieldPositionKeyPath);

  let heightOffset = 0;
  let rowPositions = getIn<TableRowPosition[]>(state, [...fieldPositionKeyPath, 'rows']).sort((a, b) => a.rowNumber - b.rowNumber);

  const previousNumberOfRows = fieldPosition.numberOfRows;
  const rowCountDifference = numberOfRows - previousNumberOfRows;

  if (rowCountDifference > 0) {
    const newRowHeight = rowPositions[rowPositions.length - 1].h;
    // add rows
    for (let rowNumber = previousNumberOfRows; rowNumber < numberOfRows; rowNumber++) {
      heightOffset += newRowHeight;
      rowPositions = insertAt(rowPositions, rowNumber, {
        rowNumber,
        h: newRowHeight
      } as TableRowPosition);
    }
  } else {
    // delete rows starting at the end
    for (let rowNumber = previousNumberOfRows - 1; rowNumber > numberOfRows - 1; rowNumber--) {
      const rowPosition = rowPositions[rowNumber];

      if (rowPosition) {
        heightOffset -= rowPosition.h;
        rowPositions = rowPositions.filter((_, idx) => idx !== rowNumber);
      }
    }
  }

  const newFieldPositionHeight = fieldPosition.h + heightOffset;

  setIn(state, [...fieldPositionKeyPath, ...['numberOfRows']], numberOfRows);
  setIn(state, [...fieldPositionKeyPath, ...['h']], newFieldPositionHeight);
  setIn(state, [...fieldPositionKeyPath, ...['rows']], rowPositions);
}

function updateFieldPositionsWithDefaultFieldPositionSettings(state: TemplateState, keyPaths: KeyPath[], useDefaultDisplaySettings: boolean): TemplateState {
  const defaultFieldPositionSettings = GetDefaultTemplateSettings(state.template.defaultFieldDisplaySettings);

  for (const keyPath of keyPaths) {
    const fieldId: string = getIn(state, [...keyPath, 'fieldId']);
    const field = getField(state, fieldId);

    // always set background color regardless of field type
    setIn(state, [...keyPath, 'backgroundColor'], defaultFieldPositionSettings.backgroundColor);

    // only update these properties for fields that use them
    if (
      field.type === FieldTypes.TextBox ||
      field.type === FieldTypes.TextArea ||
      field.type === FieldTypes.DropDown ||
      field.type === FieldTypes.CheckBox ||
      field.type === FieldTypes.CheckBoxGroup ||
      field.type === FieldTypes.Number ||
      field.type === FieldTypes.FileAttachment ||
      field.type === FieldTypes.Table ||
      field.type === FieldTypes.Date ||
      field.type === FieldTypes.DateTime ||
      field.type === FieldTypes.Time ||
      field.type === FieldTypes.Database ||
      field.type === FieldTypes.GPS ||
      field.type === FieldTypes.AutoNumber ||
      field.type === FieldTypes.Barcode ||
      field.type === FieldTypes.Audio
    ) {
      setIn(state, [...keyPath, 'align'], defaultFieldPositionSettings.align);
      setIn(state, [...keyPath, 'color'], defaultFieldPositionSettings.color);
      setIn(state, [...keyPath, 'fontName'], defaultFieldPositionSettings.fontName);
      setIn(state, [...keyPath, 'fontSize'], defaultFieldPositionSettings.fontSize);
    }

    if (
      field.type === FieldTypes.TextBox ||
      field.type === FieldTypes.TextArea ||
      field.type === FieldTypes.Number ||
      field.type === FieldTypes.Table ||
      field.type === FieldTypes.Database ||
      field.type === FieldTypes.Barcode
    ) {
      setIn(state, [...keyPath, 'focusColor'], defaultFieldPositionSettings.focusColor);
    }
  }

  //clear custom formulas
  state = updateFormula(state, keyPaths, 'Color', null);
  state = updateFormula(state, keyPaths, 'BackgroundColor', null);

  if (useDefaultDisplaySettings) {
    for (const keyPath of keyPaths) {
      setIn(state, [...keyPath, 'useDefaultDisplaySettings'], true);
    }
  }
  return state;
}

function setDuplicateNameError(state: TemplateState, message?: string): TemplateState {
  state.errorMsg = {
    title: 'Duplicate Name',
    message: message ?? 'The name you entered is already taken. Please enter another name.'
  };

  return state;
}

function setNameRequiredError(state: TemplateState, message?: string): TemplateState {
  state.errorMsg = {
    title: 'Name Required',
    message: message ?? 'A name is required. Please enter a name.'
  };

  return state;
}
